<template>
  <v2-container v-if="currentProject && teams[$route.params.team].account">
    <v2-file-viewer
      v-if="currentProject && currentProjectFiles"
      @close="showFileViewer = false"
      :files="currentProjectFiles"
      :selectedFileUid="currentFileViewerUid"
      :showFileViewer="showFileViewer"
      :timezone="teams[$route.params.team].account.timezone"
      :team="$route.params.team"
      :project="currentProject.uid"
      :getSelf="getProjectFile"
      :getImagePreview="getProjectFileImagePreview"
      :getFilePreview="getProjectFilePreview"
      :statiBits="statiBits"
      @downloadFile="
        (file) => {
          downloadFile(file);
        }
      "
    ></v2-file-viewer>
    <v2-delete-modal
      name="delete-guest-modal"
      :title="$t('projectShare.modals.deleteGuestModal.title')"
      :text="$t('projectShare.modals.deleteGuestModal.text')"
      :deleteHandler="
        () => {
          removeGuest();
        }
      "
    ></v2-delete-modal>
    <v-delete-modal
      name="delete-project-modal"
      :title="$t('modals.deleteProject.title', { name: this.currentProject.title })"
      :text="$t('modals.deleteProject.text')"
      :deleteHandler="
        () => {
          deleteProjectModalHandler(currentProject.uid);
        }
      "
    >
    </v-delete-modal>
    <v-delete-modal
      name="delete-files-modal"
      :title="
        $t('modals.deleteFile.title', {
          name: $h.getNameFromFilesByUid(currentDeleteFileUid, currentProjectFiles),
        })
      "
      :text="$t('modals.deleteFile.text')"
      :deleteHandler="
        () => {
          deleteFilesModalHandler(currentDeleteFileUid);
        }
      "
    >
    </v-delete-modal>
    <v-delete-modal
      name="delete-multiple-files-modal"
      :title="
        $tc('modals.deleteMultipleFiles.title', selectedUploadFiles.length, {
          count: selectedUploadFiles.length,
        })
      "
      :text="$tc('modals.deleteMultipleFiles.text', selectedUploadFiles.length)"
      :deleteHandler="
        () => {
          deleteMultipleFilesModalHandler();
        }
      "
    >
    </v-delete-modal>
    <v-delete-modal
      name="delete-submission-modal"
      :title="$t('modals.deleteSubmission.title')"
      :text="$t('modals.deleteSubmission.text')"
      :deleteHandler="
        () => {
          deleteSubmissionModalHandler(currentDeleteSubmissionUid);
        }
      "
    >
    </v-delete-modal>
    <v-delete-modal
      name="delete-signed-document-modal"
      :title="
        $t('signedDocuments.modals.deleteSignedDocument.title', {
          name: selectedSignedDocument.title,
        })
      "
      :text="$t('signedDocuments.modals.deleteSignedDocument.text')"
      :deleteHandler="
        () => {
          deleteSignedDocumentModalHandler();
        }
      "
    >
    </v-delete-modal>
    <v-delete-modal
      name="delete-signed-document-files-modal"
      :title="
        $t('signedDocuments.modals.deleteFile.title', {
          name: selectedSignedDocumentFile.name,
        })
      "
      :text="$t('signedDocuments.modals.deleteFile.text')"
      :deleteHandler="
        () => {
          deleteSignedDocumentFilesModalHandler();
        }
      "
    >
    </v-delete-modal>
    <modal
      name="edit-signed-document-files-modal"
      class="envivo-modal"
      @before-open="$h.toggleHtmlScroll"
      @before-close="$h.toggleHtmlScroll"
      transition="pop-out"
      height="auto"
      adaptive
      :maxWidth="600"
      width="90%"
      scrollable
    >
      <div class="modal-wrapper">
        <div class="modal-title">
          {{ $t("signedDocuments.modals.editFile") }}
        </div>
        <div class="modal-content">
          <form @submit.prevent>
            <v-textfield
              :label="$t('signedDocuments.form.placeholder.fileName')"
              v-model="signedDocumentFileName"
              v-validate="'required'"
              name="fileName"
              :error="errors.first('fileName')"
              :data-vv-as="$t('signedDocuments.form.placeholder.fileName')"
            ></v-textfield>
          </form>
        </div>
        <div class="modal-buttons">
          <a class="v1 simple" @click="$modal.hide('edit-signed-document-files-modal')">{{ $t("actions.cancel") }}</a>
          <v-button primary @click.native="editSignedDocumentFilesModalHandler()">{{ $t("actions.save") }}</v-button>
        </div>
      </div>
    </modal>
    <v2-uppy-upload
      v-if="$route.params.tab === 'uploads'"
      id="SignedDocumentsListUppyModal"
      componentTagname="pdf-only"
      :openModal="openModal"
      :params="createParams"
      :locale="teams[$route.params.team].account.locale"
      :trigger="null"
      :create-file="createSignedDocumentFile"
      :uploadConfig="uploadConfig"
      :addEvent="{
        event: 'dashboard:modal-closed',
        method: () => {
          closeSignedDocumentUploadModal();
        },
      }"
    ></v2-uppy-upload>
    <v2-row>
      <v2-col stretch>
        <v-breadcrumb :items="breadcrumbProjectItems" :mobile="breadcrumbMobileItem"></v-breadcrumb>
      </v2-col>
    </v2-row>
    <v2-row class="mb-0 pb-8">
      <v2-col stretch class="mb-2">
        <v-title-bar :title="currentProject.title">
          <v-button
            round
            primary
            icon
            @click.native="
              $goTo('QuickLaunchWithClientAndProject', {
                client: currentProject.owner.uid,
                project: currentProject.uid,
              })
            "
            v-if="!$h.isMobile()"
            class="mr-2 sm:mr-4"
            ><v2-icon
              :data="require('@icon/Icon=quick-launch.svg')"
              :original="true"
              fill
              height="30"
              width="auto"
            ></v2-icon
          ></v-button>
          <v-button round primary icon @click.native="openBuilder(true)" v-if="!$h.isMobile()" class="mr-2 sm:mr-4"
            ><v2-icon :data="require('@icon/Icon=eye.svg')" color="#fff"></v2-icon
          ></v-button>
          <v-button round secondary icon @click.native="openBuilder(false)" v-if="!$h.isMobile()" class="mr-2 sm:mr-4"
            ><v2-icon :data="require('@icon/Icon=edit-2.svg')" color="#fff" height="25" width="auto"></v2-icon
          ></v-button>
          <v-button
            round
            secondary
            icon
            @click.native="setActiveTab($t('menus.share').replace(/\s+/g, '-').toLowerCase())"
            v-if="!$h.isMobile()"
            ><v2-icon :data="require('@icon/Icon=send.svg')" color="#fff" height="28" width="auto"></v2-icon
          ></v-button>
        </v-title-bar>
        <div class="flex">
          <label
            v-if="currentProject && currentProject.owner"
            class="cursor-pointer"
            @click="$goTo('Client', { uid: currentProject.owner.uid })"
            >{{ currentProject.owner.email }}</label
          >
        </div>
        <div v-if="currentProject.untracked_visitors > 0" class="flex items-center">
          <div class="text-v2-primary-500 bg-v2-primary-100 rounded-2xl px-4 py-1 text-sm">
            {{ $t("project.tracking") }}
          </div>
          <v2-tooltip
            class="ml-2"
            :title="$t('tips.title')"
            :content="$t('tips.project.tracking')"
            v-if="showAlways || showTips"
            :show="showTips"
          ></v2-tooltip>
        </div>
      </v2-col>
      <v2-col v-if="currentProjectTags" class="col-12 mt-4">
        <v-tags :tags="currentProjectTags" v-if="!showEditTags">
          <span class="tag btn-tag" slot="customTagAfterTags" @click="toggleEditTags"
            ><v2-icon :data="require('@icon/Icon=edit-2.svg')" class="mr-1" color="#fff"></v2-icon
            >{{ $t("actions.editTags") }}</span
          >
        </v-tags>
        <div class="form-group flex" v-if="showEditTags">
          <v-select
            class="placeholder-w-full envivo-tags"
            :multiple="true"
            :placeholder="$t('placeholders.addOrSearchTags')"
            id="tags"
            :tagPlaceholder="$t('placeholders.newTag')"
            :selectLabel="$t('labels.select')"
            :selectedLabel="$t('labels.selected')"
            :deselectLabel="$t('labels.deselect')"
            v-model="tags"
            :options="tagsOptions"
            :taggable="true"
            @tag="addTag"
            :loading="loadTagsIsLoading"
            :internal-search="true"
            :hide-selected="true"
            :close-on-select="false"
            :clear-on-select="false"
            :show-no-results="false"
            :show-labels="false"
          >
            <span slot="noResult">{{ $t("labels.noDataResult") }}</span>
            <span slot="noOptions">{{ $t("labels.noDataOptions") }}</span>
            <span slot="caret" slot-scope="{ toggle }" class="multiselect-custom-caret" @mousedown.prevent.stop="toggle"
              ><v2-icon :data="require('@icon/Icon=chevron-down.svg')" width="24" height="24"></v2-icon
            ></span>
          </v-select>
          <v-button primary small class="ml-4" @click.native="toggleEditTags">{{ $t("actions.save") }}</v-button>
        </div>
      </v2-col>
    </v2-row>
    <v2-row v-if="currentProject">
      <v2-col class="col-12">
        <v-tabs :tabs="tabs" eventbus :active="getActiveTab()" :disabled="disabledTabs">
          <div :slot="$t('menus.overview')" class="mt-10">
            <v-row v-if="currentProject && currentProjectStats" id="projectStats">
              <label id="kpi" class="w-full">{{ $t("labels.kpi") }}</label>
              <v-col class="p-0">
                <router-link
                  :to="{
                    name: 'Project',
                    params: {
                      uid: currentProject.uid,
                      tab: 'history',
                      subtab: 'pageviews',
                    },
                  }"
                >
                  <v-card class="mr-6 mb-6" height="265" contentHeight="180" :title="$t('kpi.pageviews')">
                    <div slot="content" class="doughnutChartTotalNumber">
                      {{ $h.formatNumber(currentProjectStats.pageviews.total, 1) }}
                    </div>
                    <v-doughnut-chart
                      v-if="
                        pageViewsDoughnutChartData.datasets && pageViewsDoughnutChartData.datasets[0].data.length > 0
                      "
                      :data="pageViewsDoughnutChartData"
                      :options="doughnutChartOptions"
                      slot="content"
                      class="cardDoughnutChart"
                    ></v-doughnut-chart>
                  </v-card>
                </router-link>
              </v-col>
              <v-col class="p-0">
                <router-link
                  :to="{
                    name: 'Project',
                    params: {
                      uid: currentProject.uid,
                      tab: 'history',
                      subtab: 'pageviews',
                    },
                  }"
                >
                  <v-card class="mr-6 mb-6" height="265" contentHeight="180" :title="$t('kpi.visitors')">
                    <div slot="content" class="doughnutChartTotalNumber">
                      {{ $h.formatNumber(currentProjectStats.visitors.total, 1) }}
                    </div>
                    <v-doughnut-chart
                      v-if="visitorsDoughnutChartData.datasets && visitorsDoughnutChartData.datasets[0].data.length > 0"
                      :data="visitorsDoughnutChartData"
                      :options="doughnutChartOptions"
                      slot="content"
                      class="cardDoughnutChart"
                    ></v-doughnut-chart>
                  </v-card>
                </router-link>
              </v-col>
              <v-col class="p-0">
                <router-link
                  :to="{
                    name: 'Project',
                    params: {
                      uid: currentProject.uid,
                      tab: 'history',
                      subtab: 'filepreviews',
                    },
                  }"
                >
                  <v-card class="mr-6 mb-6" height="265" contentHeight="180" :title="$t('kpi.previews')">
                    <div slot="content" class="doughnutChartTotalNumber">
                      {{ $h.formatNumber(currentProjectStats.previews.total, 1) }}
                    </div>
                    <v-doughnut-chart
                      v-if="
                        filePreviewsDoughnutChartData.datasets &&
                        filePreviewsDoughnutChartData.datasets[0].data.length > 0
                      "
                      :data="filePreviewsDoughnutChartData"
                      :options="doughnutChartOptions"
                      slot="content"
                      class="cardDoughnutChart"
                    ></v-doughnut-chart>
                  </v-card>
                </router-link>
              </v-col>
              <v-col class="p-0">
                <router-link
                  :to="{
                    name: 'Project',
                    params: {
                      uid: currentProject.uid,
                      tab: 'history',
                      subtab: 'filedownloads',
                    },
                  }"
                >
                  <v-card class="mr-6 mb-6" height="265" contentHeight="180" :title="$t('kpi.downloads')">
                    <div slot="content" class="doughnutChartTotalNumber">
                      {{ $h.formatNumber(currentProjectStats.downloads.total, 1) }}
                    </div>
                    <v-doughnut-chart
                      v-if="
                        fileDownloadsDoughnutChartData.datasets &&
                        fileDownloadsDoughnutChartData.datasets[0].data.length > 0
                      "
                      :data="fileDownloadsDoughnutChartData"
                      :options="doughnutChartOptions"
                      slot="content"
                      class="cardDoughnutChart"
                    ></v-doughnut-chart>
                  </v-card>
                </router-link>
              </v-col>
              <v-col class="p-0" v-if="isVisitorStatsEnabled">
                <v-card
                  class="mr-6 mb-6"
                  height="265"
                  contentHeight="150"
                  :title="$t('labels.projectVisitedChartHeader')"
                >
                  <div slot="content">
                    <div class="v2-card-subheader px-5 pb-0.5 pb-1">
                      {{ $t("labels.projectVisitedChartSubHeader") }}
                    </div>
                    <v2-fliptimer
                      class="pt-5"
                      :duration="currentProject.sum_visit_length || 1"
                      :secondLabel="$t('labels.projectVisitedChartSeconds')"
                      :MinuteLabel="$t('labels.projectVisitedChartMinutes')"
                      :hourLabel="$t('labels.projectVisitedChartHours')"
                    ></v2-fliptimer>
                    <div
                      class="px-5 pb-0.5 pb-1 pt-1 text-xs text-danger"
                      v-if="!currentProject.sum_visit_length || !currentProject.sum_visit_length > 0"
                    >
                      {{ $t("labels.projectVisitedChartNoTime") }}
                    </div>
                  </div>
                </v-card>
              </v-col>
              <v-col class="p-0" v-if="isCustomerEngagementScoreEnabled">
                <v-card
                  class="mr-6 mb-6"
                  height="265"
                  contentHeight="150"
                  :title="$t('labels.customerEngagementScoreWidgetHeader')"
                >
                  <div slot="content">
                    <div class="v2-card-subheader px-5 pb-0.5 pb-1">
                      {{ $t("labels.customerEngagementScoreWidgetSubHeader") }}
                    </div>

                    <div
                      class="ces-full-bar mx-5 mb-1 mt-3 w-10/12"
                      style="background-color: rgb(29 78 216); height: 35px"
                    >
                      <div
                        v-if="customerEngagementScoreRange === 'daily' && dailyProjectCustomerEngagementScore > 0"
                        class="ces-actual-bar px-2 py-1"
                        :style="{
                          float: 'left',
                          color: '#ffffff',
                          width: dailyProjectCustomerEngagementScore + '%',
                          'background-color': 'rgb(147 197 253)',
                          height: '35px',
                        }"
                      >
                        <span v-if="dailyProjectCustomerEngagementScore > 50">
                          {{ dailyProjectCustomerEngagementScore }} / 100
                        </span>
                      </div>

                      <div
                        v-if="customerEngagementScoreRange === 'total' && totalProjectCustomerEngagementScore > 0"
                        class="ces-actual-bar px-2 py-1"
                        :style="{
                          float: 'left',
                          color: '#ffffff',
                          width: totalProjectCustomerEngagementScore + '%',
                          'background-color': 'rgb(147 197 253)',
                          height: '35px',
                        }"
                      >
                        <span v-if="totalProjectCustomerEngagementScore > 50">
                          {{ totalProjectCustomerEngagementScore }} / 100
                        </span>
                      </div>

                      <div
                        class="px-2 py-1"
                        v-if="customerEngagementScoreRange === 'daily' && dailyProjectCustomerEngagementScore < 50"
                        style="color: #ffffff; float: right"
                      >
                        {{ dailyProjectCustomerEngagementScore }} / 100
                      </div>

                      <div
                        class="px-2 py-1"
                        v-if="customerEngagementScoreRange === 'total' && totalProjectCustomerEngagementScore < 50"
                        style="color: #ffffff; float: right"
                      >
                        {{ totalProjectCustomerEngagementScore }} / 100
                      </div>
                    </div>

                    <div class="px-5 pb-0.5 pb-1 pt-7 text-xs">
                      {{ $t("labels.customerEngagementScoreWidgetRange") }}
                      <a
                        :class="['ml-3', customerEngagementScoreRange !== 'daily' ? 'underline' : '']"
                        :style="{
                          color: customerEngagementScoreDailyLinkColor,
                        }"
                        @click="switchCustomerEngagementScoreRange('daily')"
                      >
                        {{ $t("labels.customerEngagementScoreWidgetRangeDaily") }}
                      </a>
                      <a
                        :class="['ml-3', customerEngagementScoreRange !== 'total' ? 'underline' : '']"
                        :style="{
                          color: customerEngagementScoreTotalLinkColor,
                        }"
                        @click="switchCustomerEngagementScoreRange('total')"
                      >
                        {{ $t("labels.customerEngagementScoreWidgetRangeTotal") }}
                      </a>
                    </div>
                  </div>
                </v-card>
              </v-col>
            </v-row>
            <label id="projectStatus">{{ $t("labels.projectStatus") }}</label>
            <v-row v-if="currentProject">
              <v-col class="col-12 md:col-6 status-col" v-if="currentProjectStati && currentProjectStati.ARCHIVED">
                <v-project-status
                  statusKey="STATUS"
                  :projectStatus="
                    (currentProject.status & currentProjectStati.ARCHIVED.bit) === currentProjectStati.ARCHIVED.bit
                  "
                  :locale="'de'"
                  :status="currentProjectStati.ARCHIVED.status"
                >
                </v-project-status>
              </v-col>
              <v-col class="col-12 md:col-6 status-col" v-if="currentProjectStati && currentProjectStati.POSTED">
                <v-project-status
                  statusKey="POSTED"
                  :projectStatus="
                    (currentProject.status & currentProjectStati.POSTED.bit) === currentProjectStati.POSTED.bit
                  "
                  :locale="'de'"
                  :status="currentProjectStati.POSTED.status"
                >
                </v-project-status>
              </v-col>
              <v-col class="col-12 md:col-6 status-col" v-if="currentProjectStati && currentProjectStati.VISITED">
                <v-project-status
                  statusKey="VISITED"
                  :projectStatus="
                    (currentProject.status & currentProjectStati.VISITED.bit) === currentProjectStati.VISITED.bit
                  "
                  :locale="'de'"
                  :status="currentProjectStati.VISITED.status"
                >
                </v-project-status>
              </v-col>
              <v-col class="col-12 md:col-6 status-col" v-if="currentProjectStats">
                <v-project-status statusKey="UPLOADS" :projectStatus="currentProjectStats.uploads.total > 0">
                </v-project-status>
              </v-col>
              <v-col class="col-12 md:col-6 status-col" v-if="currentProjectStats">
                <v-project-status statusKey="DOWNLOADS" :projectStatus="currentProjectStats.downloads.total > 0">
                </v-project-status>
              </v-col>
            </v-row>
            <v-activity-feed-group
              :activityFeedItems="activityFeedTeaserItems"
              :timezone="teams[$route.params.team].account.timezone"
              type="all"
              :customLabel="$t('labels.currentActivities')"
              :loadMore="true"
              :loadMoreText="$t('actions.showMore')"
              @loadMore="
                $goTo('Project', {
                  uid: $route.params.uid,
                  tab: 'history',
                  subtab: 'all',
                })
              "
              hideProject
              hideClient
              withoutLink
              perProject
            />
          </div>
          <div :slot="$t('menus.infos')" class="mt-10">
            <v-row v-if="currentProject" id="projectInfo">
              <v-col class="col-12 px-0 flex justify-end">
                <v-button lavender fixedWidth @click.native="toggleProjectDataForm()">
                  <v2-icon
                    v-if="!editForm"
                    :data="require('@icon/Icon=edit-2.svg')"
                    width="18"
                    height="18"
                    class="mr-1"
                  ></v2-icon
                  >{{ editForm ? $t("actions.save") : $t("actions.edit") }}</v-button
                >
              </v-col>
              <v-col class="col-12 p-0">
                <ul class="list">
                  <v-list-item simple>
                    <v-list-cell :title="$t('project.title')" size="3" />
                    <v-list-cell size="2">
                      <div slot="value" class="w-full">
                        <span v-if="!editTitle && !titleIsLoading">{{ currentProject.title }}</span>
                        <v-textfield
                          simple
                          name="title"
                          :error="errors.first('title')"
                          v-validate="'required'"
                          v-if="editTitle && !titleIsLoading"
                          v-model="title"
                          data-vv-scope="editProject"
                        ></v-textfield>
                        <v-pulse-loader
                          v-if="titleIsLoading"
                          :loading="titleIsLoading"
                          :color="'#343e93'"
                          :size="'10px'"
                        ></v-pulse-loader>
                      </div>
                    </v-list-cell>
                    <v-list-cell emptyValue />
                  </v-list-item>
                  <v-list-item simple>
                    <v-list-cell :title="$t('project.client')" size="3" />
                    <v-list-cell
                      :value="$h.formatName(currentProject.owner)"
                      :valueRoute="{
                        name: 'Client',
                        params: { uid: currentProject.owner.uid },
                      }"
                      size="2"
                    />
                    <v-list-cell emptyValue />
                  </v-list-item>
                  <v-list-item simple>
                    <v-list-cell :title="$t('project.template')" size="3" />
                    <v-list-cell :value="currentProject.template.name" size="2" />
                    <v-list-cell emptyValue />
                  </v-list-item>
                  <v-list-item simple>
                    <v-list-cell :title="$t('client.createdDate')" size="3" />
                    <v-list-cell
                      :value="$h.formatDate(currentProject.created_at, teams[$route.params.team].account.timezone)"
                      size="2"
                    />
                    <v-list-cell emptyValue />
                  </v-list-item>
                  <v-list-item simple>
                    <v-list-cell :title="$t('project.lastActivity')" size="3" />
                    <v-list-cell
                      :value="
                        currentProject.last_active_at
                          ? $h.formatDate(
                              currentProject.last_active_at,
                              teams[$route.params.team].account.timezone,
                              true
                            )
                          : ''
                      "
                      size="2"
                    />
                    <v-list-cell emptyValue />
                  </v-list-item>
                  <v-list-item simple>
                    <v-list-cell :title="$t('project.contact')" size="3" />
                    <v-list-cell size="2">
                      <div slot="value" class="w-full">
                        <span v-if="!editContact && !contactIsLoading">
                          {{ currentProject.contact ? $h.formatName(currentProject.contact) : "" }}</span
                        >
                        <v2-select
                          withoutFormError
                          :placeholder="$t('placeholders.selectContact')"
                          v-if="editContact && !contactIsLoading"
                          v-model="contact"
                          :options="contactOptions"
                          optionLabel="last_name"
                          id="contact"
                          name="contact"
                          :searchable="true"
                          :loading="isLoading"
                          :internalSearch="true"
                          openDir="bottom"
                          :customLabel="setCustomLabel"
                        ></v2-select>
                        <v-pulse-loader
                          v-if="contactIsLoading"
                          :loading="contactIsLoading"
                          :color="'#343e93'"
                          :size="'10px'"
                        ></v-pulse-loader>
                      </div>
                    </v-list-cell>
                    <v-list-cell emptyValue />
                  </v-list-item>
                  <v-list-item v-if="reassignProjectsEnabled" simple flexibleHeight>
                    <v-list-cell :title="$t('project.user')" size="3" />
                    <v-list-cell size="2" :truncate="false">
                      <div slot="value" class="w-full">
                        <span v-if="!editUser && !userIsLoading" class="nl2br">
                          {{ currentProject.user.last_name }}, {{ currentProject.user.first_name }}
                        </span>
                        <v2-select
                          withoutFormError
                          v-if="editUser && !userIsLoading"
                          v-model="user"
                          :options="userOptions"
                          optionLabel="last_name"
                          id="user"
                          name="user"
                          :searchable="true"
                          :loading="isLoading"
                          :internalSearch="true"
                          openDir="bottom"
                          :customLabel="setCustomLabel"
                        ></v2-select>
                        <v-pulse-loader
                          v-if="userIsLoading"
                          :loading="userIsLoading"
                          :color="'#343e93'"
                          :size="'10px'"
                        ></v-pulse-loader>
                      </div>
                    </v-list-cell>
                    <v-list-cell emptyValue />
                  </v-list-item>
                  <v-list-item simple flexibleHeight>
                    <v-list-cell :title="$t('project.notes')" size="3" />
                    <v-list-cell size="2" :truncate="false">
                      <div slot="value" class="w-full">
                        <span
                          class="placeholder"
                          v-if="!editNotes && !notesIsLoading && (!currentProject.notes || currentProject.notes === '')"
                        >
                          {{ $t("placeholders.noNotes") }}</span
                        >
                        <span v-if="!editNotes && !notesIsLoading" class="nl2br">{{ currentProject.notes }}</span>
                        <v-textarea
                          simple
                          v-if="editNotes && !notesIsLoading"
                          v-model="notes"
                          rows="1"
                          grow
                        ></v-textarea>
                        <v-pulse-loader
                          v-if="notesIsLoading"
                          :loading="notesIsLoading"
                          :color="'#343e93'"
                          :size="'10px'"
                        ></v-pulse-loader>
                      </div>
                    </v-list-cell>
                    <v-list-cell emptyValue />
                  </v-list-item>
                </ul>
              </v-col>
              <v-col class="col-12 px-0 mt-8 flex justify-end">
                <v-button lavender fixedWidth @click.native="toggleProjectDataForm()">
                  <v2-icon
                    v-if="!editForm"
                    :data="require('@icon/Icon=edit-2.svg')"
                    width="18"
                    height="18"
                    class="mr-1"
                  ></v2-icon
                  >{{ editForm ? $t("actions.save") : $t("actions.edit") }}</v-button
                >
              </v-col>
            </v-row>
          </div>
          <div :slot="$t('menus.share')" class="mt-10">
            <div class="flex justify-between">
              <label id="send" class="px-4">{{ $t("projectShare.titles.share") }}</label>
              <div>
                <a class="secondary" @click="copyLink(currentProject.links.frontend)">
                  <v2-icon :data="require('@icon/Icon=share-2.svg')"></v2-icon>
                  {{ $t("projectShare.labels.copyProjectLink") }}
                </a>
                <a
                  class="secondary ml-8"
                  v-if="teams[$route.params.team].account.team.attributes.portal_enabled"
                  @click="copyLink(currentProject.owner.links.portal)"
                >
                  <v2-icon :data="require('@icon/Icon=share-2.svg')"></v2-icon>
                  {{ $t("projectShare.labels.copyPortalLink") }}
                </a>
              </div>
            </div>
            <div class="caption caption-c text-v2-neutral-500 px-4 mt-4 mb-10">
              {{ $t("projectShare.labels.sendProject") }}
            </div>
            <v-row v-if="currentProject" style="min-height: 504px">
              <v-col class="col-12 p-0" v-if="currentProject.uid === $route.params.uid">
                <v2-project-share
                  v-if="templateSnippetsReady"
                  :project="currentProject"
                  :email-snippets="templateSnippetOptions"
                  :default-snippet="defaultTemplateSnippet"
                  :hasPortalEnabled="teams[$route.params.team].account.team.attributes.portal_enabled"
                  @sentMail="
                    (data) => {
                      setSentStatus(data);
                    }
                  "
                ></v2-project-share>
                <hr class="mx-4 border-v2-neutral-300" />
              </v-col>
            </v-row>
            <v2-row>
              <v2-col col12 noSpaceX>
                <v2-row>
                  <v2-col col12>
                    <v2-project-link
                      :link="currentProject.links.frontend"
                      :label="$t('quickLaunch.labels.projectLink')"
                      :clickCallback="
                        () => {
                          copyLink(currentProject.links.frontend);
                        }
                      "
                    ></v2-project-link>
                  </v2-col>
                  <v2-col col12 v-if="teams[$route.params.team].account.team.attributes.portal_enabled">
                    <v2-project-link
                      :link="currentProject.owner.links.portal"
                      :label="$t('quickLaunch.labels.portalLink')"
                      :clickCallback="
                        () => {
                          copyLink(currentProject.owner.links.portal);
                        }
                      "
                    ></v2-project-link>
                  </v2-col>
                </v2-row>
                <hr class="mx-4 border-v2-neutral-300" />
              </v2-col>
            </v2-row>
            <div class="caption caption-c text-v2-neutral-500 px-4 mb-10">
              {{ $t("projectShare.labels.shareProject") }}
            </div>
            <v-row>
              <v-col class="col-12">
                <p class="paragraph-2">{{ $t("projectShare.labels.addClient") }}</p>
                <div class="flex">
                  <v2-select
                    :placeholder="$t('placeholders.searchForClient')"
                    v-model="projectGuest"
                    :options="clientOptions"
                    id="client"
                    name="client"
                    optionLabel="last_name"
                    v-validate="'required'"
                    data-vv-scope="addGuest"
                    :searchable="true"
                    :loading="isLoading"
                    :internalSearch="false"
                    @searchChange="loadClients"
                    :customLabel="setCustomLabel"
                    openDir="bottom"
                  >
                  </v2-select>
                  <v2-button secondary class="ml-4" @click.native="addGuest">{{ $t("actions.add") }}</v2-button>
                </div>
                <ul style="background-color: rgba(241, 242, 246, 1)" class="p-4">
                  <li class="flex flex-wrap bg-white px-4 py-3 w-full my-2 justify-between items-center">
                    <div>
                      <span>{{ $h.formatName(currentProject.owner) }} {{ $t("projectShare.labels.owner") }}</span>
                      &nbsp;&middot;&nbsp;
                      <span class="text-v2-neutral-500 text-sm">{{ currentProject.owner.email }}</span>
                    </div>
                  </li>
                  <li
                    v-for="guest in currentProjectGuests"
                    :key="guest.uid"
                    class="flex flex-wrap bg-white px-4 py-3 w-full my-2 justify-between items-center"
                  >
                    <div>
                      <span>{{ $h.formatName(guest) }} {{ $t("projectShare.labels.guest") }}</span>
                      &nbsp;&middot;&nbsp;
                      <span class="text-v2-neutral-500 text-sm">{{ guest.email }}</span>
                    </div>
                    <div class="flex items-center">
                      <a class="secondary mr-2" @click="copyLink(guest.links.frontend, false)">
                        <v2-icon :data="require('@icon/Icon=share-2.svg')"></v2-icon>
                        {{ $t("projectShare.labels.copyProjectLink") }}
                      </a>
                      <v2-button
                        ghostProgress
                        icon
                        iconWarning
                        sm
                        class="ml-2"
                        @click.native="openDeleteGuestModal(guest)"
                      >
                        <v2-icon
                          :data="require('@icon/Icon=trash.svg')"
                          width="20px"
                          height="20px"
                          color="inherit"
                        ></v2-icon>
                      </v2-button>
                    </div>
                  </li>
                </ul>
              </v-col>
            </v-row>
          </div>
          <div :slot="$t('menus.uploads')" class="mt-10">
            <h5 class="w-full">{{ $t("labels.clientUploads") }}</h5>
            <v-row id="files" v-if="currentProject && currentProjectFiles && teamsSettings[$route.params.team]">
              <v-col class="col-12 p-0">
                <v-list-header complex>
                  <v-list-header-cell
                    class="cursor-pointer"
                    icon="Icon=trash.svg"
                    iconWidth="19"
                    iconColor="red"
                    size="0"
                    @iconClicked="deleteSelectedUploadFiles"
                  />
                  <v-list-header-cell emptyIcon />
                  <v-list-header-cell
                    :showAs="$t('file.title')"
                    size="3"
                    :showAsDropdownUid="$h.createUniqueUid()"
                    :showAsDropdown="uploads.sortMainColDropdown"
                  />
                  <v-list-header-cell :title="$t('file.extension')" tabletHidden />
                  <v-list-header-cell :title="$t('file.size')" />
                  <v-list-header-cell :title="$t('file.category')" />
                  <v-list-header-cell
                    dropdownRight
                    :showAs="$t('file.created')"
                    :showAsDropdownUid="$h.createUniqueUid()"
                    :showAsDropdown="uploads.showAsThirdColDropdown"
                  />
                  <v-list-header-cell emptyIcon />
                </v-list-header>
                <ul class="list cursor-pointer">
                  <upload-list-item
                    v-for="file in currentProjectFiles"
                    :key="file.uid"
                    :file="file"
                    :moreDropdown="moreDropdown"
                    :timezone="teams[$route.params.team].account.timezone"
                    :statiBits="statiBits"
                    @openFileViewer="(uid) => openFileViewer(uid)"
                    :getSelf="getProjectFile"
                    :currentProject="currentProject"
                    :getPreview="getProjectFileImagePreview"
                    :team="$route.params.team"
                    :selected="selectedUploadFiles"
                    @check="selectFile(file)"
                    @uncheck="selectFile(file)"
                  >
                  </upload-list-item>
                  <v-list-item v-if="!currentProjectFiles.length">
                    <v-list-cell :value="$t('placeholders.noFiles')"></v-list-cell>
                  </v-list-item>
                </ul>
                <v-list-pagination
                  :currentPage="teamsSettings[$route.params.team].listsSettings.uploads.pagination.page"
                  :showPerPage="teamsSettings[$route.params.team].listsSettings.uploads.pagination.perPage"
                  :totalPages="teamsSettings[$route.params.team].listsSettings.uploads.pagination.lastPage"
                  :apiCall="
                    (page, perPage) => {
                      changeProjectFilesPage(page, perPage);
                    }
                  "
                >
                </v-list-pagination>
              </v-col>
            </v-row>
            <v-list-header
              simple
              v-if="
                currentProject &&
                teams[$route.params.team] &&
                teams[$route.params.team].account &&
                teams[$route.params.team].account.team.attributes.signed_documents &&
                teams[$route.params.team].account.team.attributes.signed_documents.signing_presets
              "
            >
              <v-list-header-cell :title="$t('signedDocuments.titles.main')" />
              <v-list-header-cell :simpleLink="$t('signedDocuments.create')" :onClick="closeUppyAndRedirect" />
            </v-list-header>
            <v-row
              v-if="
                currentProject &&
                teams[$route.params.team] &&
                teams[$route.params.team].account &&
                teams[$route.params.team].account.team.attributes.signed_documents &&
                teams[$route.params.team].account.team.attributes.signed_documents.signing_presets
              "
            >
              <v-col stretch class="p-0">
                <v-list-header complex>
                  <v-list-header-cell
                    :mobile="$t('actions.sort')"
                    :mobileDropdownUid="$h.createUniqueUid()"
                    showAlways
                    :mobileDropdown="signedDocuments.mobileDropdown"
                  />
                </v-list-header>
                <signed-document-item
                  v-for="signedDocument in signedDocumentBundles"
                  :key="signedDocument.uid"
                  :signedDocument="signedDocument"
                  :files="signedDocumentBundleFiles[signedDocument.uid]"
                  :getSelf="getSignedDocument"
                  :currentProject="currentProject.uid"
                  :statiBits="statiBits"
                  :signedDocumentStatiBits="signedDocumentStatiBits"
                  :downloadFile="
                    (file) => {
                      downloadSignedDocumentFile(file);
                    }
                  "
                  :team="teams[$route.params.team]"
                  :signedDocumentFileMoreDropdown="signedDocumentFileMoreDropdown"
                  :timezone="teams[$route.params.team].account.timezone"
                  :signedDocumentFileMoreDropdownWithDisabled="signedDocumentFileMoreDropdownWithDisabled"
                  :deleteDocument="
                    (data) => {
                      showSignedDocumentDeleteModal(data);
                    }
                  "
                  :startSigningProcess="
                    (data) => {
                      startSigningProcess(data);
                    }
                  "
                  :redirectToSigningPage="
                    (data) => {
                      redirectToSigningPage(data);
                    }
                  "
                  :addFiles="
                    (data) => {
                      showSignedDocumentUploadModal(data);
                    }
                  "
                  :getSignedDocumentFile="getSignedDocumentFile"
                  :getSignedDocumentFilePreview="getSignedDocumentFilePreview"
                >
                </signed-document-item>
                <v-list-item v-if="!signedDocumentBundles.length">
                  <v-list-cell :value="$t('signedDocuments.noSignedDocuments')"></v-list-cell>
                </v-list-item>
                <v-list-pagination
                  :currentPage="teamsSettings[$route.params.team].listsSettings.signedDocuments.pagination.page"
                  :showPerPage="teamsSettings[$route.params.team].listsSettings.signedDocuments.pagination.perPage"
                  :totalPages="teamsSettings[$route.params.team].listsSettings.signedDocuments.pagination.lastPage"
                  :showPerPageOptions="[5, 10, 15]"
                  :apiCall="
                    (page, perPage) => {
                      changeSignedDocumentsPage(page, perPage);
                    }
                  "
                >
                </v-list-pagination>
              </v-col>
            </v-row>
          </div>
          <template v-slot:afterLabelSlot-4 v-if="!hasCrmFieldsEnabled">
            <v2-tooltip
              class="ml-2"
              :title="$t('tips.title')"
              :content="$t('tips.project.disabledTab')"
              v-if="showAlways || showTips"
              :show="showTips"
            ></v2-tooltip>
          </template>
          <div :slot="$t('menus.crm')" v-if="hasCrmFieldsEnabled">
            <v-row>
              <v-col stretch class="p-0">
                <v-col class="col-12 px-0 flex justify-end">
                  <v-button lavender fixedWidth @click.native="toggleCrmFields()">
                    <v2-icon
                      v-if="!editCrmFields"
                      :data="require('@icon/Icon=edit-2.svg')"
                      width="18"
                      height="18"
                      class="mr-1"
                    ></v2-icon
                    >{{ editCrmFields ? $t("actions.save") : $t("actions.edit") }}</v-button
                  >
                </v-col>
                <ul
                  class="list"
                  style="border-top: none; margin-bottom: 3rem"
                  v-for="(group, i) in filteredCurrentProjectCrmFields"
                  :key="i"
                >
                  <label :id="$h.getLanguageValue(group.title)" class="w-full" style="margin-left: 10px">{{
                    $h.getLanguageValue(group.title)
                  }}</label>
                  <v-list-item
                    flexibleHeight
                    simple
                    v-for="(crmField, key, index) in group.fields"
                    :key="index"
                    :class="{ 'border-t-2': index === 0 }"
                  >
                    <v-list-cell :title="getCrmFieldTitle(crmField, key)" size="3" />
                    <v-list-cell
                      v-if="!editCrmField[key] && !crmFieldIsLoading[key]"
                      :has_html="true"
                      :value="getCrmFieldValue(crmField, key, currentProject)"
                      size="2"
                      :placeholder="getCrmFieldPlaceholder(crmField, key)"
                      :truncate="false"
                    />
                    <v-list-cell size="2" v-if="editCrmField[key] && !crmFieldIsLoading[key]">
                      <div slot="value" class="w-full">
                        <v2-select
                          withoutFormError
                          :placeholder="$t('placeholders.selectCustom')"
                          v-if="editCrmField[key] && !crmFieldIsLoading[key] && crmField.type === 'select'"
                          v-model="crmFieldValue[key]"
                          :options="crmFieldSelectOptions[key]"
                          optionLabel="label"
                          :searchable="true"
                          openDir="bottom"
                        ></v2-select>
                        <v2-select
                          withoutFormError
                          :placeholder="$t('placeholders.selectCustom')"
                          v-if="editCrmField[key] && !crmFieldIsLoading[key] && crmField.type === 'multiselect'"
                          :closeOnSelect="false"
                          :clearOnSelect="false"
                          :hideSelected="true"
                          v-model="crmFieldValue[key]"
                          :options="crmFieldSelectOptions[key]"
                          optionLabel="label"
                          trackBy="label"
                          :multiple="true"
                          :searchable="true"
                          openDir="bottom"
                        >
                        </v2-select>
                        <date-picker
                          class="envivo-datetimepicker"
                          v-if="editCrmField[key] && !crmFieldIsLoading[key] && crmField.type === 'datetime'"
                          v-model="crmFieldValue[key]"
                          :lang="teams[$route.params.team].account.locale"
                          type="datetime"
                          :formatter="datetimeFormat"
                          :minute-step="1"
                          :first-day-of-week="1"
                          :show-second="false"
                        >
                        </date-picker>
                        <v-textfield
                          simple
                          v-if="editCrmField[key] && !crmFieldIsLoading[key] && crmField.type === 'input'"
                          v-model="crmFieldValue[key]"
                        ></v-textfield>
                        <v-pulse-loader
                          v-if="crmFieldIsLoading[key]"
                          :loading="crmFieldIsLoading[key]"
                          :color="'#343e93'"
                          :size="'10px'"
                        ></v-pulse-loader>
                      </div>
                    </v-list-cell>
                    <v-list-cell emptyValue />
                  </v-list-item>
                </ul>
              </v-col>
            </v-row>
          </div>
          <div :slot="$t('menus.forms')">
            <v-row>
              <v-col stretch class="p-0">
                <v-breadcrumb class="justify-between" :items="[]" :mobile="false">
                  <v2-search
                    v-if="!currentProjectFormSubmission"
                    :placeholder="$t('settings.forms.placeholders.search')"
                    maxWidth
                    @reset="search('projectFormSubmissions', getSubmissions)"
                    @submit="search('projectFormSubmissions', getSubmissions)"
                    v-model="searchQuery"
                  ></v2-search>
                  <a
                    class="secondary flex items-center"
                    @click="resetCurrentProjectFormSubmissions"
                    v-if="currentProjectFormSubmission"
                    ><v2-icon :data="require('@icon/Icon=chevron-left.svg')" width="20px" height="20px"></v2-icon
                    >{{ $t("settings.forms.actions.goBack") }}</a
                  >
                </v-breadcrumb>
              </v-col>
            </v-row>
            <v-row v-if="projectFormSubmissions && !currentProjectFormSubmission">
              <v-col class="col-12 p-0">
                <v-list-header complex>
                  <v-list-header-cell
                    size="2"
                    :showAs="$t('settings.forms.labels.submissionDate')"
                    :showAsDropdownUid="$h.createUniqueUid()"
                    :showAsDropdown="projectFormSubmissionsDropdownData.sortMainColDropdown"
                  />
                  <v-list-header-cell :title="$t('settings.forms.labels.project')" />
                  <v-list-header-cell :title="$t('settings.forms.labels.client')" />
                  <v-list-header-cell
                    :mobile="$t('actions.sort')"
                    :mobileDropdownUid="$h.createUniqueUid()"
                    :mobileDropdown="projectFormSubmissionsDropdownData.mobileDropdown"
                  />
                  <v-list-header-cell
                    filter
                    dropdownRight
                    multiselect
                    :filterActive="teamsSettings[$route.params.team].listsSettings.projectFormSubmissions.filterActive"
                    :filterDropdownUid="$h.createUniqueUid()"
                    :filterDropdown="projectFormSubmissionsDropdownData.filterDropdown"
                  />
                </v-list-header>
                <ul class="list" v-if="projectFormSubmissions">
                  <v-list-item
                    class="cursor-pointer"
                    complex
                    hover
                    @click.native="setCurrentSubmission(submission)"
                    v-for="submission of projectFormSubmissions"
                    :key="submission.uid"
                  >
                    <v-list-cell
                      :title="$h.formatDate(submission.created_at, teams[$route.params.team].account.timezone)"
                      size="2"
                      :mobileSubtitle="submission.project_title + ' - ' + submission.client_name"
                    />
                    <v-list-cell :value="submission.project_title" />
                    <v-list-cell :value="submission.client_name" />
                    <v-list-cell emptyIcon></v-list-cell>
                    <v-list-cell
                      iconDropdown
                      icon="Icon=more-horizontal.svg"
                      iconColor="#5057af"
                      :dropdownData="submissionMoreDropdown"
                      :dropdownUid="$h.createUniqueUid()"
                      :dropdownValue="submission.uid"
                    />
                  </v-list-item>
                  <v-list-item v-if="!(projectFormSubmissions && projectFormSubmissions.length)">
                    <v-list-cell :value="$t('placeholders.noFormSubmissions')"></v-list-cell>
                  </v-list-item>
                </ul>
                <v-list-pagination
                  :currentPage="teamsSettings[$route.params.team].listsSettings.projectFormSubmissions.pagination.page"
                  :totalPages="
                    teamsSettings[$route.params.team].listsSettings.projectFormSubmissions.pagination.lastPage
                  "
                  :showPerPage="
                    teamsSettings[$route.params.team].listsSettings.projectFormSubmissions.pagination.perPage
                  "
                  :apiCall="
                    (page, perPage) => {
                      changeProjectFormSubmissionsPage(page, perPage);
                    }
                  "
                >
                </v-list-pagination>
              </v-col>
            </v-row>
            <v2-row noSpace v-if="currentProjectFormSubmission">
              <v-col class="p-0">
                <h2 class="heading-3 text-v2-secondary-400">
                  {{
                    $t("settings.forms.titles.submission", {
                      date: $h.formatDate(
                        currentProjectFormSubmission.created_at,
                        teams[$route.params.team].account.timezone
                      ),
                    })
                  }}
                </h2>
              </v-col>
            </v2-row>
            <v-row v-if="currentProjectFormSubmission && !currentProjectFormSubmission.fields.length">
              <v-col class="px-0 pt-6">
                <div>
                  {{ $t("settings.forms.placeholders.noFormResponses") }}
                </div>
              </v-col>
            </v-row>
            <v-row v-if="currentProjectFormSubmission">
              <v-col class="col-12 p-0">
                <v-row>
                  <v-col
                    class="col-12 pb-0 px-0"
                    :class="f.field.type !== 'checkbox' ? 'pt-6' : 'pt-2'"
                    v-for="f in currentProjectFormSubmission.fields"
                    :key="f.uid"
                  >
                    <div v-if="f.field.type === 'htmlblock' && f.field.options.show_in_backoffice">
                      <div
                        class="text-v2-secondary-400 font-bold"
                        v-html="$h.getLanguageValue(f.field.options.value)"
                      ></div>
                    </div>
                    <div v-if="f.field.type === 'input'">
                      <div class="text-v2-secondary-400 font-bold">
                        {{ $h.getLanguageValue(f.field.options.label) }}
                      </div>
                      <v2-textfield :value="f.value" disabled withoutFormError></v2-textfield>
                    </div>
                    <div v-if="f.field.type === 'textarea'">
                      <div class="text-v2-secondary-400 font-bold">
                        {{ $h.getLanguageValue(f.field.options.label) }}
                      </div>
                      <v2-textarea :value="f.value" disabled withoutFormError></v2-textarea>
                    </div>
                    <div v-if="f.field.type === 'select'">
                      <div class="text-v2-secondary-400 font-bold">
                        {{ $h.getLanguageValue(f.field.options.label) }}
                      </div>
                      <v2-select :value="f.value" :options="[]" disabled withoutFormError></v2-select>
                    </div>
                    <div v-if="f.field.type === 'checkbox'">
                      <v2-checkbox
                        :value="transformCheckboxValue(f.value)"
                        :label="$h.getLanguageValue(f.field.options.label)"
                        disabled
                        withoutFormError
                      ></v2-checkbox>
                    </div>

                    <div v-if="f.field.type === 'checkbox-group'">
                      <div class="text-v2-secondary-400 font-bold">
                        {{ $h.getLanguageValue(f.field.options.label) }}
                      </div>
                      <div class="flex items-center pt-2">
                        <v2-checkbox
                          v-for="option in f.field.options.options"
                          :key="option.name"
                          :value="JSON.parse(f.value)[option.name]"
                          :label="$h.getLanguageValue(option.label)"
                          class="pr-4"
                          disabled
                          withoutFormError
                        ></v2-checkbox>
                      </div>
                    </div>

                    <div v-if="f.field.type === 'radio-group'">
                      <div class="text-v2-secondary-400 font-bold">
                        {{ $h.getLanguageValue(f.field.options.label) }}
                      </div>
                      <div class="flex items-center pt-2">
                        <div class="v2-input v2-radio-group">
                          <div v-for="option in f.field.options.options" :key="option.name">
                            <label class="radio" :for="f.field.name"
                              >{{ $h.getLanguageValue(option.label) }}
                              <input type="radio" :id="f.field.name" :checked="option.name === f.value" disabled />
                              <span class="checkmark"></span>
                              <slot></slot>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </div>
          <div :slot="$t('menus.activityFeeds')">
            <v-tabs :tabs="activityFeedTabs" eventbus :active="getActiveActivityFeedsTab()" class="mt-10">
              <div :slot="activityFeedTabs[0]">
                <v-activity-feed-group
                  :customLabel="$t('labels.allActivities')"
                  :activityFeedItems="activityFeedItems.items"
                  :timezone="teams[$route.params.team].account.timezone"
                  type="all"
                  hideProject
                  hideClient
                  withoutLink
                  perProject
                  :loadMoreIsLoading="loadMoreIsLoading"
                  :loadMore="!!activityFeedItems.data.next_chunk_from"
                  @loadMore="loadMore('Sorted', activityFeedItems.data.next_chunk_from)"
                >
                  <label slot="beforeTitle" v-if="currentLineChartData">{{ $t("labels.activitiesChart") }}</label>
                  <div slot="beforeTitle" class="flex" v-if="!lineChartIsLoading && currentLineChartData">
                    <v-button
                      primary
                      small
                      icon
                      @click.native="
                        lineChartCurrentDayIndex !== activityFeedLineChart.days.length - 1
                          ? (lineChartCurrentDayIndex += 1)
                          : false
                      "
                      :disabled="lineChartCurrentDayIndex === activityFeedLineChart.days.length - 1"
                      :active="lineChartCurrentDayIndex !== activityFeedLineChart.days.length - 1"
                    >
                      <v2-icon :data="require('@icon/Icon=chevron-left.svg')"></v2-icon>
                    </v-button>
                    <div class="mx-4">
                      {{
                        $moment(activityFeedLineChart.days[lineChartCurrentDayIndex])
                          .tz(teams[$route.params.team].account.timezone)
                          .format("L")
                      }}
                    </div>
                    <v-button
                      primary
                      small
                      icon
                      @click.native="lineChartCurrentDayIndex !== 0 ? (lineChartCurrentDayIndex -= 1) : false"
                      :disabled="lineChartCurrentDayIndex === 0"
                      :active="lineChartCurrentDayIndex !== 0"
                    >
                      <v2-icon :data="require('@icon/Icon=chevron-right.svg')"></v2-icon>
                    </v-button>
                  </div>
                  <div
                    slot="beforeTitle"
                    v-if="lineChartIsLoading"
                    style="height: 50px; position: relative; z-index: 9999"
                  >
                    <v-spinner :loading="lineChartIsLoading" zIndex="9999"></v-spinner>
                  </div>
                  <v-line-chart
                    v-if="!lineChartIsLoading && currentLineChartData"
                    slot="beforeTitle"
                    :data="{
                      labels: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23],
                      datasets: [
                        {
                          data: currentLineChartData,
                          fill: false,
                          borderColor: 'rgba(80, 87, 175, 1)',
                        },
                      ],
                    }"
                    :options="lineChartOptions"
                    class="activity-line-chart"
                  ></v-line-chart>
                </v-activity-feed-group>
              </div>
              <div :slot="activityFeedTabs[1]">
                <v-activity-feed-group
                  :activityFeedItems="activityFeedFileUploads.items"
                  :timezone="teams[$route.params.team].account.timezone"
                  type="fileUploads"
                  hideProject
                  hideClient
                  withoutLink
                  perProject
                  :loadMoreIsLoading="loadMoreIsLoading"
                  :loadMore="!!activityFeedFileUploads.data.next_chunk_from"
                  @loadMore="loadMore('FileUpload', activityFeedFileUploads.data.next_chunk_from)"
                />
              </div>
              <div :slot="activityFeedTabs[2]">
                <v-activity-feed-group
                  :activityFeedItems="activityFeedFileDownloads.items"
                  :timezone="teams[$route.params.team].account.timezone"
                  type="fileDownloads"
                  hideProject
                  hideClient
                  withoutLink
                  perProject
                  :loadMoreIsLoading="loadMoreIsLoading"
                  :loadMore="!!activityFeedFileDownloads.data.next_chunk_from"
                  @loadMore="loadMore('FileDownload', activityFeedFileDownloads.data.next_chunk_from)"
                >
                  <v-horizontal-bar-chart
                    v-if="fileDownloadsBarChartData.datasets && fileDownloadsBarChartData.datasets[0].data.length > 0"
                    :data="fileDownloadsBarChartData"
                    :options="barChartOptions"
                    slot="afterTitle"
                    class="activityChart"
                    :style="'height: ' + calcChartHeight(fileDownloadsBarChartData.datasets[0].data.length) + 'px;'"
                  >
                  </v-horizontal-bar-chart>
                </v-activity-feed-group>
              </div>
              <div :slot="activityFeedTabs[3]">
                <v-activity-feed-group
                  :activityFeedItems="activityFeedFilePreviews.items"
                  :timezone="teams[$route.params.team].account.timezone"
                  type="filePreviews"
                  hideProject
                  hideClient
                  withoutLink
                  perProject
                  :loadMoreIsLoading="loadMoreIsLoading"
                  :loadMore="!!activityFeedFilePreviews.data.next_chunk_from"
                  @loadMore="loadMore('FilePreview', activityFeedFilePreviews.data.next_chunk_from)"
                >
                  <v-horizontal-bar-chart
                    v-if="filePreviewsBarChartData.datasets && filePreviewsBarChartData.datasets[0].data.length > 0"
                    :data="filePreviewsBarChartData"
                    :options="barChartOptions"
                    slot="afterTitle"
                    class="activityChart"
                    :style="'height: ' + calcChartHeight(filePreviewsBarChartData.datasets[0].data.length) + 'px;'"
                  >
                  </v-horizontal-bar-chart>
                </v-activity-feed-group>
              </div>
              <div :slot="activityFeedTabs[4]">
                <v-activity-feed-group
                  :activityFeedItems="activityFeedPageViews.items"
                  :timezone="teams[$route.params.team].account.timezone"
                  type="pageViews"
                  hideProject
                  hideClient
                  withoutLink
                  perProject
                  :loadMoreIsLoading="loadMoreIsLoading"
                  :loadMore="!!activityFeedPageViews.data.next_chunk_from"
                  @loadMore="loadMore('PageView', activityFeedPageViews.data.next_chunk_from)"
                >
                  <v-horizontal-bar-chart
                    v-if="pageViewsBarChartData.datasets && pageViewsBarChartData.datasets[0].data.length > 0"
                    :data="pageViewsBarChartData"
                    :options="barChartOptions"
                    slot="afterTitle"
                    class="activityChart"
                    :style="'height: ' + calcChartHeight(pageViewsBarChartData.datasets[0].data.length) + 'px;'"
                  >
                  </v-horizontal-bar-chart>
                </v-activity-feed-group>
              </div>
              <div :slot="activityFeedTabs[5]">
                <v-activity-feed-group
                  :activityFeedItems="activityFeedProjectMessages.items"
                  :timezone="teams[$route.params.team].account.timezone"
                  type="projectMessages"
                  hideProject
                  hideClient
                  withoutLink
                  perProject
                  :loadMoreIsLoading="loadMoreIsLoading"
                  :loadMore="!!activityFeedProjectMessages.data.next_chunk_from"
                  @loadMore="loadMore('ProjectMessage', activityFeedProjectMessages.data.next_chunk_from)"
                />
              </div>
              <div :slot="activityFeedTabs[6]">
                <v-activity-feed-group
                  :activityFeedItems="activityFeedProjectFileSignings.items"
                  :timezone="teams[$route.params.team].account.timezone"
                  type="projectFileSignings"
                  hideProject
                  hideClient
                  withoutLink
                  perProject
                  :loadMoreIsLoading="loadMoreIsLoading"
                  :loadMore="!!activityFeedProjectFileSignings.data.next_chunk_from"
                  @loadMore="loadMore('ProjectFileSigned', activityFeedProjectFileSignings.data.next_chunk_from)"
                >
                </v-activity-feed-group>
              </div>
            </v-tabs>
          </div>
          <div :slot="$t('menus.extended-data')" class="mt-10">
            <v-row v-if="currentProject" id="customProjectInfo">
              <v-col class="col-12 p-0">
                <div class="mb-12">
                  <v-list-item simpleSwitch v-for="(status, index) in filteredCurrentProjectStati" :key="index">
                    <v-list-cell size="3" class="title">
                      <div slot="value" class="flex items-center">
                        {{
                          status.title && status.title[teams[$route.params.team].account.locale]
                            ? status.title[teams[$route.params.team].account.locale]
                            : status.title["default"]
                        }}
                        <div
                          class="help-tooltip"
                          v-tooltip.top="
                            status.description && status.description[teams[$route.params.team].account.locale]
                              ? status.description[teams[$route.params.team].account.locale]
                              : status.description['default']
                          "
                        >
                          <v2-icon
                            :data="require('@icon/Icon=info.svg')"
                            width="20"
                            height="20"
                            color="#CECECE"
                          ></v2-icon>
                        </div>
                      </div>
                    </v-list-cell>
                    <v-list-cell size="2" style="margin-right: 10px">
                      <v-switch
                        slot="value"
                        v-model="customStatus[index]"
                        :name="index"
                        @input="toggleStatus(index, customStatus[index])"
                      ></v-switch>
                    </v-list-cell>
                    <v-list-cell emptyValue></v-list-cell>
                  </v-list-item>
                </div>
                <v-col class="col-12 px-0 flex justify-end">
                  <v-button lavender fixedWidth @click.native="toggleCustomFields()">
                    <v2-icon
                      v-if="!editCustomFields"
                      :data="require('@icon/Icon=edit-2.svg')"
                      width="18"
                      height="18"
                      class="mr-1"
                    ></v2-icon
                    >{{ editCustomFields ? $t("actions.save") : $t("actions.edit") }}</v-button
                  >
                </v-col>
                <ul class="list">
                  <v-list-item
                    flexibleHeight
                    simple
                    v-for="(customField, index) in filteredCurrentProjectCustomFields"
                    :key="index"
                  >
                    <v-list-cell :title="getCustomFieldTitle(customField, index)" size="3" />
                    <v-list-cell
                      v-if="!editCustomField[index] && !customFieldIsLoading[index]"
                      :has_html="true"
                      :value="getCustomFieldValue(customField, index, currentProject)"
                      size="2"
                      :placeholder="getCustomFieldPlaceholder(customField, index)"
                      :truncate="false"
                    />
                    <v-list-cell size="2" v-if="editCustomField[index] && !customFieldIsLoading[index]">
                      <div slot="value" class="w-full">
                        <v2-select
                          withoutFormError
                          :placeholder="$t('placeholders.selectCustom')"
                          v-if="editCustomField[index] && !customFieldIsLoading[index] && customField.type === 'select'"
                          v-model="customFieldValue[index]"
                          :options="customFieldSelectOptions[index]"
                          optionLabel="label"
                          :searchable="true"
                          openDir="bottom"
                        ></v2-select>
                        <v2-select
                          withoutFormError
                          :placeholder="$t('placeholders.selectCustom')"
                          v-if="
                            editCustomField[index] && !customFieldIsLoading[index] && customField.type === 'multiselect'
                          "
                          :closeOnSelect="false"
                          :clearOnSelect="false"
                          :hideSelected="true"
                          v-model="customFieldValue[index]"
                          :options="customFieldSelectOptions[index]"
                          optionLabel="label"
                          trackBy="label"
                          :multiple="true"
                          :searchable="true"
                          openDir="bottom"
                        >
                        </v2-select>
                        <date-picker
                          class="envivo-datetimepicker"
                          v-if="
                            editCustomField[index] && !customFieldIsLoading[index] && customField.type === 'datetime'
                          "
                          v-model="customFieldValue[index]"
                          :lang="teams[$route.params.team].account.locale"
                          type="datetime"
                          :formatter="datetimeFormat"
                          :minute-step="1"
                          :first-day-of-week="1"
                          :show-second="false"
                        >
                        </date-picker>
                        <v-textfield
                          simple
                          v-if="editCustomField[index] && !customFieldIsLoading[index] && customField.type === 'input'"
                          v-model="customFieldValue[index]"
                        ></v-textfield>
                        <v-pulse-loader
                          v-if="customFieldIsLoading[index]"
                          :loading="customFieldIsLoading[index]"
                          :color="'#343e93'"
                          :size="'10px'"
                        ></v-pulse-loader>
                      </div>
                    </v-list-cell>
                    <v-list-cell emptyValue />
                  </v-list-item>
                </ul>
                <v-col class="col-12 px-0 mt-8 flex justify-end">
                  <v-button lavender fixedWidth @click.native="toggleCustomFields()">
                    <v2-icon
                      v-if="!editCustomFields"
                      :data="require('@icon/Icon=edit-2.svg')"
                      width="18"
                      height="18"
                      class="mr-1"
                    ></v2-icon
                    >{{ editCustomFields ? $t("actions.save") : $t("actions.edit") }}</v-button
                  >
                </v-col>
              </v-col>
            </v-row>
          </div>
          <div :slot="$t('menus.fileapproval')" v-if="isFileApprovalEnabled" class="mt-10">
            <v2-row>
              <v2-col col12 noSpaceX>
                <div v-if="!createNewFileApprovalActive">
                  <label id="fileapproval" class="w-full">{{ $t("fileapproval.labels.header") }}</label>
                  <hr class="my-5" />
                  <div class="mb-4">
                    <span>{{
                      $tc("fileapproval.labels.subheader", fileApprovals.length, { count: fileApprovals.length })
                    }}</span>
                    <v2-button
                      primary
                      sm
                      outline
                      @click.native="createNewFileApprovalActive = true"
                      style="float: right"
                      :fullWidth="isSmView"
                      :class="{ 'mb-4': isSmView }"
                    >
                      {{ $t("fileapproval.buttons.new") }}
                    </v2-button>
                  </div>
                  <div v-if="fileApprovals.length > 0">
                    <v2-file-approval
                      v-for="(fileApproval, index) in fileApprovals"
                      :key="fileApproval.uid"
                      :isActive="index === 0"
                      :fileApproval="fileApproval"
                      :project-uid="currentProject.uid"
                      :timezone="teams[$route.params.team].account.timezone"
                      :locale="teams[$route.params.team].account.locale"
                    >
                    </v2-file-approval>
                  </div>
                </div>
                <div v-if="createNewFileApprovalActive">
                  <div @click="createNewFileApprovalActive = false" class="inline-block cursor-pointer">
                    <v2-icon
                      style="margin-bottom: 8px"
                      :data="require('@icon/Icon=arrow-left.svg')"
                      width="24px"
                      height="24px"
                    ></v2-icon>
                  </div>
                  <span class="ml-3 text-2xl">{{ $t("fileapproval.labels.newFileApprovalHeader") }}</span>

                  <v2-textfield
                    class="mt-2"
                    :label="$t('fileapproval.labels.title')"
                    :placeholder="$t('fileapproval.placeholders.title')"
                    v-model="newFileApprovalTitle"
                    v-validate="'required|regex:^[0-9]+'"
                    name="newFileApprovalTitle"
                    :error="errors.first('newFileApprovalTitle')"
                    :data-vv-as="$t('fileapproval.labels.title')"
                    @keyup.native.enter="createFileApproval"
                  ></v2-textfield>
                  <v2-button primary sm outline @click.native="resetNewFileApprovalFormAndGoBack">
                    {{ $t("fileapproval.buttons.cancel") }}
                  </v2-button>
                  <v2-button
                    primary
                    sm
                    @click.native="createFileApproval"
                    :active="newFileApprovalTitle.length > 0"
                    class="ml-2"
                  >
                    {{ $t("fileapproval.buttons.create") }}
                  </v2-button>
                </div>
              </v2-col>
            </v2-row>
          </div>
          <div :slot="$t('menus.settings')" class="mt-10">
            <v-row>
              <v-col class="col-12 p-0">
                <v-list-item simpleSwitch>
                  <v-list-cell size="3" class="title">
                    <div slot="value" class="flex items-center">
                      {{ $t("titles.projectPw") }}
                      <div class="help-tooltip" v-tooltip.top="{ content: $t('texts.projectPw') }">
                        <v2-icon
                          :data="require('@icon/Icon=info.svg')"
                          width="20"
                          height="20"
                          color="#CECECE"
                        ></v2-icon>
                      </div>
                    </div>
                  </v-list-cell>
                  <v-list-cell size="2">
                    <v-switch
                      slot="value"
                      v-model="pwProtected"
                      name="pwProtected"
                      @input="togglePwProtected"
                    ></v-switch>
                  </v-list-cell>
                  <v-list-cell emptyValue></v-list-cell>
                </v-list-item>
                <v-list-item simpleSwitch>
                  <v-list-cell size="3" class="title">
                    <div slot="value" class="flex items-center">
                      {{ $t("titles.projectPwClient") }}
                      <div class="help-tooltip" v-tooltip.top="{ content: $t('texts.projectPwClient') }">
                        <v2-icon
                          :data="require('@icon/Icon=info.svg')"
                          width="20"
                          height="20"
                          color="#CECECE"
                        ></v2-icon>
                      </div>
                    </div>
                  </v-list-cell>
                  <v-list-cell size="2">
                    <v-switch
                      slot="value"
                      v-model="pwProtectedClient"
                      name="pwProtectedClient"
                      @input="togglePwProtectedClient"
                    ></v-switch>
                  </v-list-cell>
                  <v-list-cell emptyValue></v-list-cell>
                </v-list-item>
                <v-list-item simpleSwitch>
                  <v-list-cell size="3" class="title">
                    <div slot="value" class="flex items-center">
                      {{ $t("titles.activityFeeds") }}
                      <div
                        class="help-tooltip"
                        v-tooltip.top="{
                          content: $t('texts.activityFeedsProject'),
                        }"
                      >
                        <v2-icon
                          :data="require('@icon/Icon=info.svg')"
                          width="20"
                          height="20"
                          color="#CECECE"
                        ></v2-icon>
                      </div>
                    </div>
                  </v-list-cell>
                  <v-list-cell size="2">
                    <v-switch
                      slot="value"
                      v-model="isSubscribed"
                      name="isSubscribed"
                      @input="toggleSubscription"
                    ></v-switch>
                  </v-list-cell>
                  <v-list-cell emptyValue></v-list-cell>
                </v-list-item>
                <v-list-item simpleSwitch>
                  <v-list-cell size="3" class="title">
                    <div slot="value" class="flex items-center">
                      <v2-icon
                        class="mr-1"
                        v-if="!teamCan('project-archived')"
                        :data="require('@icon/Icon=lock.svg')"
                      ></v2-icon>
                      {{ $t("titles.archiveProject") }}
                      <div class="help-tooltip" v-tooltip.top="{ content: $t('texts.archiveProject') }">
                        <v2-icon
                          :data="require('@icon/Icon=info.svg')"
                          width="20"
                          height="20"
                          color="#CECECE"
                        ></v2-icon>
                      </div>
                    </div>
                  </v-list-cell>
                  <v-list-cell size="2">
                    <v-switch
                      slot="value"
                      v-model="isArchived"
                      name="isArchived"
                      :disabled="!teamCan('project-archived')"
                      @input="toggleStatus('ARCHIVED', isArchived)"
                    ></v-switch>
                  </v-list-cell>
                  <v-list-cell emptyValue></v-list-cell>
                </v-list-item>
              </v-col>
            </v-row>
            <v-row v-if="currentProject">
              <v-col class="p-0">
                <h5>{{ $t("labels.deleteProject") }}</h5>
                <v-button danger @click.native="showProjectDeleteModal()">{{ $t("actions.deleteProject") }}</v-button>
              </v-col>
            </v-row>
          </div>
        </v-tabs>
      </v2-col>
    </v2-row>
  </v2-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import listSettings from "@/mixins/listSettings.js";
import signedDocuments from "@/mixins/dropdowns/signedDocuments.js";
import uploads from "@/mixins/dropdowns/uploads.js";
import customFields from "@/mixins/customFields.js";
import crmFields from "@/mixins/crmFields.js";
import teamAbilities from "@/mixins/teamAbilities.js";
import UploadListItem from "@/views/partials/UploadListItem";
import SignedDocumentItem from "@/views/partials/SignedDocumentItem";
import globalMixin from "@/mixins/global.js";
import FileApproval from "@/views/partials/fileApproval/FileApproval";
import forms from "@/mixins/dropdowns/forms.js";
import * as clipboard from "clipboard-polyfill";

export default {
  name: "Project",
  components: {
    UploadListItem,
    SignedDocumentItem,
    "v2-file-approval": FileApproval,
  },
  mixins: [globalMixin, listSettings, signedDocuments, uploads, customFields, crmFields, teamAbilities, forms],
  data() {
    return {
      selectedUploadFiles: [],
      createNewFileApprovalActive: false,
      newFileApprovalTitle: "",
      activityFeedTeaserItems: [],
      templateThumbnailBig: require("@/assets/svg/template-thumbnail-big.svg"),
      currentDeleteFileUid: null,
      currentDeleteSubmissionUid: null,
      fileDownloadsBarChartData: null,
      fileDownloadsDoughnutChartData: null,
      filePreviewsBarChartData: null,
      filePreviewsDoughnutChartData: null,
      pageViewsBarChartData: null,
      pageViewsDoughnutChartData: null,
      visitorsDoughnutChartData: null,
      lineChartIsLoading: false,
      lineChartCurrentDayIndex: 0,
      allLineChartData: null,
      initLineChartData: {
        //
      },
      initBarChartData: {
        labels: [],
        extraLabels: [],
        datasets: [
          {
            data: [],
            backgroundColor: [
              "#2F98E8",
              "#154469",
              "#76B9EC",
              "#EAD3B8",
              "#AC998C",
              "#615480",
              "#C2D2BE",
              "#42B8B8",
              "#8E5CFF",
              "#C2C4C8",
              "#462E80",
            ],
            align: "start",
          },
        ],
      },
      initDoughnutChartData: {
        labels: [],
        extraLabels: [],
        datasets: [
          {
            data: [],
            backgroundColor: [
              "#2F98E8",
              "#154469",
              "#76B9EC",
              "#EAD3B8",
              "#AC998C",
              "#615480",
              "#C2D2BE",
              "#42B8B8",
              "#8E5CFF",
              "#C2C4C8",
              "#462E80",
            ],
          },
        ],
      },
      lineChartOptions: {
        maintainAspectRatio: false,
        legend: {
          display: false,
        },
        scales: {
          yAxes: [
            {
              ticks: {
                max: 0,
              },
            },
          ],
        },
        tooltips: {
          enabled: false,
        },
        plugins: {
          datalabels: {
            font: {
              family: "Cabin",
            },
            align: -50,
            anchor: "start",
            offset: 0,
          },
        },
      },
      barChartOptions: {
        maintainAspectRatio: false,
        legend: {
          display: false,
        },
        scales: {
          yAxes: [
            {
              gridLines: {
                display: false,
              },
              barThickness: 20,
              ticks: {
                fontFamily: "Cabin",
                fontStyle: "bold",
              },
            },
          ],
          xAxes: [
            {
              gridLines: {
                color: "#eee",
              },
              ticks: {
                beginAtZero: true,
                userCallback: (label) => {
                  if (Math.floor(label) === label) {
                    return label;
                  }
                },
              },
            },
          ],
        },
        tooltips: {
          position: "custom",
          backgroundColor: "#FFFFFF",
          bodyFontColor: "#000000",
          borderWidth: 1,
          borderColor: "#eee",
          bodyFontFamily: "Cabin",
          displayColors: false,
          callbacks: {
            label: (tooltipItem, data) => {
              let label = data.extraLabels[tooltipItem.index] || "";
              let value = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
              return value + " " + label;
            },
            title: () => {
              return "";
            },
          },
        },
        plugins: {
          datalabels: {
            font: {
              family: "Cabin",
            },
            align: -45,
            anchor: "start",
            offset: 14,
            formatter: (value, context) => {
              return context.chart.data.extraLabels[context.dataIndex];
            },
          },
        },
      },
      doughnutChartOptions: {
        maintainAspectRatio: false,
        legend: {
          display: false,
        },
        tooltips: {
          callbacks: {
            label: (tooltipItem, data) => {
              let label = data.extraLabels[tooltipItem.index] || "";
              let value = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
              return value + " " + label;
            },
            title: () => {
              return "";
            },
          },
          enabled: false,
          custom: function (tooltipModel) {
            // Tooltip Element
            var tooltipEl = document.getElementById("chartjs-tooltip");
            // Create element on first render
            if (!tooltipEl) {
              tooltipEl = document.createElement("div");
              tooltipEl.id = "chartjs-tooltip";
              tooltipEl.innerHTML = "<table></table>";
              document.body.appendChild(tooltipEl);
            }
            // Hide if no tooltip
            if (tooltipModel.opacity === 0) {
              tooltipEl.style.opacity = 0;
              return;
            }
            // Set caret Position
            tooltipEl.classList.remove("above", "below", "no-transform");
            if (tooltipModel.yAlign) {
              tooltipEl.classList.add(tooltipModel.yAlign);
            } else {
              tooltipEl.classList.add("no-transform");
            }
            function getBody(bodyItem) {
              return bodyItem.lines;
            }
            // Set Text
            if (tooltipModel.body) {
              var titleLines = tooltipModel.title || [];
              var bodyLines = tooltipModel.body.map(getBody);
              var innerHtml = "<thead>";
              titleLines.forEach(function (title) {
                innerHtml += "<tr><th>" + title + "</th></tr>";
              });
              innerHtml += "</thead><tbody>";
              bodyLines.forEach(function (body, i) {
                var colors = tooltipModel.labelColors[i];
                var style = "background:" + colors.backgroundColor;
                style += " border-color:" + colors.borderColor;
                style += " border-width: 2px";
                var span = '<span style="' + style + '"></span>';
                innerHtml += "<tr><td>" + span + body + "</td></tr>";
              });
              innerHtml += "</tbody>";
              var tableRoot = tooltipEl.querySelector("table");
              tableRoot.innerHTML = innerHtml;
            }
            // `this` will be the overall tooltip
            var position = this._chart.canvas.getBoundingClientRect();
            // Display, position, and set styles for font
            tooltipEl.style.backgroundColor = "#333333";
            tooltipEl.style.color = "#ffffff";
            tooltipEl.style.maxWidth = "180px";
            tooltipEl.style.zIndex = "1000";
            tooltipEl.style.opacity = 1;
            tooltipEl.style.position = "absolute";
            tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + "px";
            tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + "px";
            tooltipEl.style.fontFamily = "Cabin";
            tooltipEl.style.fontSize = tooltipModel.bodyFontSize + "px";
            tooltipEl.style.fontStyle = tooltipModel._bodyFontStyle;
            tooltipEl.style.padding = tooltipModel.yPadding + "px " + tooltipModel.xPadding + "px";
            tooltipEl.style.pointerEvents = "none";
          },
        },
        plugins: {
          datalabels: {
            display: false,
          },
        },
      },
      order: {
        col: "",
        dir: "",
      },
      editForm: false,
      editCustomFields: false,
      editCrmFields: false,
      editTitle: false,
      titleIsLoading: false,
      title: "",
      editContact: false,
      contactIsLoading: false,
      contact: "",
      contactOptions: [],
      defaultTemplateSnippet: {},
      templateSnippetOptions: [],
      templateSnippetsReady: false,
      isLoading: false,
      editNotes: false,
      notesIsLoading: false,
      notes: "",
      editUser: false,
      userIsLoading: false,
      user: "",
      userOptions: [],
      pwProtected: false,
      pwProtectedClient: false,
      newMessage: "",
      isSubscribed: false,
      isArchived: false,
      customStatus: {},
      currentProjectFilesPreviews: [],
      loadMoreIsLoading: false,
      switchModel: {},
      showEditTags: false,
      tags: [],
      tagsOptions: [],
      loadTagsIsLoading: false,
      editTagsIsLoading: false,
      customerEngagementScoreRange: "daily",
      fileApprovals: [],
      tabs: [
        this.$t("menus.overview"),
        this.$t("menus.infos"),
        this.$t("menus.share"),
        this.$t("menus.uploads"),
        this.$t("menus.crm"),
        this.$t("menus.activityFeeds"),
        this.$t("menus.extended-data"),
        {
          label: this.$t("menus.chat"),
          method: () => {
            this.setRedirectedInboxProjectAndRedirect();
          },
        },
        this.$t("menus.settings"),
      ],
      activityFeedTabs: [
        this.$t("menus.all"),
        this.$t("menus.fileUploads"),
        this.$t("menus.fileDownloads"),
        this.$t("menus.filePreview"),
        this.$t("menus.pageViews"),
        this.$t("menus.projectMessages"),
        this.$t("menus.projectFileSignings"),
      ],
      moreDropdown: [
        {
          options: [
            {
              label: this.$t("actions.delete"),
              method: (uid) => {
                this.showFileDeleteModal(uid);
              },
              color: "text-danger",
              doNotShowActiveState: true,
            },
          ],
        },
      ],
      submissionMoreDropdown: [
        {
          options: [
            {
              label: this.$t("actions.delete"),
              method: (uid) => {
                this.showSubmissionDeleteModal(uid);
              },
              color: "text-danger",
              doNotShowActiveState: true,
            },
          ],
        },
      ],
      breadcrumbProjectItems: [
        {
          label: this.$t("menus.projects"),
          route: { name: "Projects" },
        },
      ],
      breadcrumbMobileItem: {
        label: this.$t("menus.projects"),
        route: { name: "Projects" },
      },
      // signedDocumentMoreDropdown: [
      //   {
      //     options: [
      //       {
      //         label: this.$t('signedDocuments.actions.sign'),
      //         method: (data) => { this.showSignedDocumentDeleteModal(data) },
      //         doNotShowActiveState: true
      //       },
      //       {
      //         label: this.$t('actions.delete'),
      //         method: (data) => { this.showSignedDocumentDeleteModal(data) },
      //         color: 'text-danger',
      //         doNotShowActiveState: true
      //       }
      //     ]
      //   }
      // ],
      signedDocumentFileMoreDropdown: [
        {
          options: [
            {
              label: this.$t("actions.edit"),
              method: (data) => {
                this.showSignedDocumentFileEditModal(data);
              },
              doNotShowActiveState: true,
              disabled: false,
            },
            {
              label: this.$t("actions.delete"),
              method: (data) => {
                this.showSignedDocumentFileDeleteModal(data);
              },
              color: "text-danger",
              doNotShowActiveState: true,
            },
          ],
        },
      ],
      signedDocumentFileMoreDropdownWithDisabled: [
        {
          options: [
            {
              label: this.$t("actions.edit"),
              method: () => {},
              doNotShowActiveState: true,
              disabled: true,
            },
            {
              label: this.$t("actions.delete"),
              method: () => {},
              color: "text-danger",
              doNotShowActiveState: true,
              disabled: true,
            },
          ],
        },
      ],
      selectedSignedDocument: {},
      selectedSignedDocumentFile: {},
      signedDocumentFileName: "",
      createParams: {},
      uploadConfig: {
        max_files: 1,
      },
      openModal: false,
      currentSignedDocument: {},
      showFileViewer: false,
      currentFileViewerUid: "",
      formsDropdownOptions: [
        {
          options: [
            {
              label: this.$t("actions.delete"),
              method: (template) => {
                this.$goTo("editTemplate", { uid: template.uid });
              },
              doNotShowActiveState: true,
            },
            {
              label: this.$t("actions.export"),
              method: (template) => {
                this.openTemplateBuilder(template.uid, false);
              },
              doNotShowActiveState: true,
            },
          ],
        },
      ],
      searchQuery: "",
      clientOptions: [],
      projectGuest: null,
      currentDeleteGuest: null,
    };
  },
  created() {
    let vm = this;

    if (vm.$route.query.redirect === "templatebuilder") {
      const url = vm.$h.removeURLParameter(window.location.href, "redirect");
      window.history.pushState("", "", url);
      vm.openBuilder(false);
    }
    // check if currentProject has changed, if so, do some magic
    if (this.previousCurrentProject && this.previousCurrentProject.uid !== this.$route.params.uid) {
      this.resetCurrentProjectUploads();
      this.resetCurrentProjectFormSubmissions();
    }

    vm.initChartsData();
    vm.$store.commit("PROJECTS_RESET_ACTIVITY_FEEDS");

    if (vm.isFileApprovalEnabled) {
      let settingTabIdx = vm.tabs.findIndex(function (tabLabel, idx) {
        if (tabLabel === vm.$t("menus.settings")) {
          return idx;
        }
      });

      vm.tabs.splice(settingTabIdx, 0, vm.$t("menus.fileapproval"));
    }
    if (vm.isFormsEnabled) {
      let settingTabIdx = vm.tabs.findIndex(function (tabLabel, idx) {
        if (tabLabel === vm.$t("menus.activityFeeds")) {
          return idx;
        }
      });

      vm.tabs.splice(settingTabIdx, 0, vm.$t("menus.forms"));
    }

    this.loadPageData(this.$route.params.uid);
    this.uploadConfig.max_size = this.teamUploadConfig().max_size;
  },
  mounted() {
    // hide tooltip if still present
    const el = document.getElementById("chartjs-tooltip");
    if (el) el.style.opacity = "0";

    if (this.isFileApprovalEnabled) {
      this.$eventbus.$on("fileApprovalListReloadEvent", this.fileApprovalListReloadListener);
    }

    this.$eventbus.$on("setActiveTab", this.setActiveTab);
    this.$eventbus.$on("loadMessagesAndSetRead", this.loadMessagesAndSetRead);
  },
  destroyed() {
    this.$eventbus.$off("setActiveTab");
    this.$eventbus.$off("loadMessagesAndSetRead");

    if (this.isFileApprovalEnabled) {
      this.$eventbus.$off("fileApprovalListReloadEvent");
    }
  },
  methods: {
    ...mapActions([
      "setListsSettings",
      "setMenus",
      "getProject",
      "getProjectMessages",
      "getProjectFiles",
      "getProjectStats",
      "sendProjectMessage",
      "editProject",
      "deleteProject",
      "getProjectFileImagePreview",
      "getProjectFilePreview",
      "downloadProjectFile",
      "deleteProjectFile",
      "getTeamContacts",
      "getProjectBuilderSession",
      "getProjectSubscription",
      "postProjectSubscription",
      "deleteProjectSubscription",
      "setProjectPosted",
      "getProjectsActivityFeeds",
      "getTemplateSnippets",
      "getProjectTags",
      "getTeamTags",
      "getProjectFile",
      "fileApprovalApiRequest",
      "createFileApprovalRequest",
      "deleteFileApprovalRequest",
      "getSignedDocumentBundles",
      "getSignedDocument",
      "deleteSignedDocument",
      "getSignedDocumentFile",
      "deleteSignedDocumentFile",
      "editSignedDocumentFile",
      "getSignedDocumentFilePreview",
      "createSignedDocumentFiles",
      "getDownloadSignedDocumentFileUrl",
      "startSignedDocumentSigningProcess",
      "getSignedDocumentSigningUrl",
      "setNotificationStatus",
      "setVisitorSync",
      "setVisitorSyncTimestamp",
      "setVisitorSyncRunning",
      "setRedirectedInboxProject",
      "getProjectFormSubmissions",
      "getForms",
      "getClientList",
      "getProjectFormSubmission",
      "getUsers",
      "deleteProjectFormSubmission",
      "getProjectGuests",
      "addProjectGuest",
      "deleteProjectGuest",
      "editClient",
    ]),
    loadPageData(uid) {
      const vm = this;
      vm.$store.commit("GLOBAL_CUSTOM_LOADING_STARTED");
      vm.getProject({ project: uid }).then(() => {
        this.$store.commit("PROJECTS_SET_PREVIOUS_CURRENT_PROJECT", this.currentProject);
        vm.breadcrumbProjectItems.push({
          label: vm.currentProject.title,
          active: true,
        });
        vm.isArchived =
          (vm.currentProject.status & vm.currentProjectStati.ARCHIVED.bit) === vm.currentProjectStati.ARCHIVED.bit;
        Object.keys(vm.currentProjectStati).forEach((key) => {
          if (!vm.currentProjectStati[key].readonly && key !== "ARCHIVED") {
            vm.customStatus[key] =
              (vm.currentProject.status & vm.currentProjectStati[key].bit) === vm.currentProjectStati[key].bit;
          }
        });
        vm.setEditableCustomFields(vm.currentProjectCustomFields, vm.currentProject);
        if (vm.currentProjectCrmFields) {
          vm.currentProjectCrmFields.forEach((group) => {
            vm.setEditableCrmFields(group.fields, vm.currentProject);
          });
        }
        vm.title = vm.currentProject.title;

        // set default value for sum_visited_length
        if (!vm.currentProject.sum_visited_length) {
          vm.currentProject.sum_visited_length = 0;
        }

        vm.pwProtected = vm.currentProject.private;
        vm.pwProtectedClient = vm.currentProject.privacy_by_client_choice;
        vm.notes = vm.currentProject.notes;
        vm.contact = vm.currentProject.contact;
        vm.user = vm.currentProject.user;
        const uid = vm.currentProject.uid;
        vm.getProjectTags({ project: uid }).then(() => {
          this.formatTagsOptions(JSON.parse(JSON.stringify(this.currentProjectTags)));
          this.tags = JSON.parse(JSON.stringify(this.tagsOptions));
          this.initTagOptions();
        });
        // overview
        if (vm.$route.params.tab === "overview") {
          const p1 = vm.getProjectStats({ project: uid }).then(() => {
            if (vm.currentProjectStats.downloads.aggregated) {
              vm.setChartData(vm.currentProjectStats.downloads.aggregated, vm.fileDownloadsBarChartData);
              vm.setChartData(vm.currentProjectStats.downloads.aggregated, vm.fileDownloadsDoughnutChartData);
            }
            if (vm.currentProjectStats.pageviews.aggregated) {
              vm.setChartData(vm.currentProjectStats.pageviews.aggregated, vm.pageViewsBarChartData);
              vm.setChartData(vm.currentProjectStats.pageviews.aggregated, vm.pageViewsDoughnutChartData);
            }
            if (vm.currentProjectStats.previews.aggregated) {
              vm.setChartData(vm.currentProjectStats.previews.aggregated, vm.filePreviewsBarChartData);
              vm.setChartData(vm.currentProjectStats.previews.aggregated, vm.filePreviewsDoughnutChartData);
            }
            vm.visitorsDoughnutChartData.datasets[0].data.push(vm.currentProjectStats.visitors.total);
          });
          const p2 = vm
            .getProjectsActivityFeeds({
              type: "Sorted",
              params: { project_uid: uid },
            })
            .then(() => {
              this.activityFeedTeaserItems = this.activityFeedItems.items.slice(0, 5);
            });
          Promise.all([p1, p2])
            .then(() => {
              vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
            })
            .catch(() => {
              vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
            });
        } else if (vm.$route.params.tab === "data") {
          const contactParams = {
            per_page: "1000",
            order_col: "last_name",
            order_dir: "ASC",
            search: "",
          };
          vm.getTeamContacts({ params: contactParams, customLoading: true })
            .then((res) => {
              vm.contactOptions = res.data.data;

              const userParams = {
                per_page: "1000",
                order_col: "last_name",
                order_dir: "ASC",
                search: "",
              };

              vm.getUsers({ params: userParams, customLoading: true })
                .then((res) => {
                  vm.userOptions = res.data.data;

                  vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
                })
                .catch(() => {
                  vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
                });
            })
            .catch(() => {
              vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
            });
        } else if (vm.$route.params.tab === "share") {
          const p1 = vm
            .getTemplateSnippets({
              template: vm.currentProject.template.uid,
              params: {
                per_page: "1000",
                is_email: true,
              },
            })
            .then((res) => {
              vm.templateSnippetOptions = res.data.data;
              vm.defaultTemplateSnippet = vm.templateSnippetOptions.find((t) => {
                return vm.$h.templateSnippetsStatus(t.status, "default", false);
              });
              vm.templateSnippetsReady = true;
            });
          const p2 = vm.getProjectGuests({ project: vm.currentProject.uid });
          const params = {
            per_page: "1000",
            order_col: "last_name",
            order_dir: "ASC",
            search: "",
          };
          const p3 = vm.getClientList({ params, customLoading: true }).then((res) => {
            this.clientOptions = res;
          });

          Promise.all([p1, p2, p3])
            .then(() => {
              vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
            })
            .catch(() => {
              vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
            });
        } else if (vm.$route.params.tab === "uploads") {
          const p1 = vm.getProjectFiles({
            project: uid,
            setListsSettings: true,
            params: {
              type: "client",
              page: this.teamsSettings[this.$route.params.team].listsSettings.uploads.pagination.page,
              per_page: this.teamsSettings[this.$route.params.team].listsSettings.uploads.pagination.perPage,
              order_col: this.teamsSettings[this.$route.params.team].listsSettings.uploads.order.col,
              order_dir: this.teamsSettings[this.$route.params.team].listsSettings.uploads.order.dir,
            },
          });
          const p2 = vm.getSignedDocumentBundles({
            project: uid,
            setListsSettings: true,
            params: {
              page: this.teamsSettings[this.$route.params.team].listsSettings.signedDocuments.pagination.page,
              per_page: this.teamsSettings[this.$route.params.team].listsSettings.signedDocuments.pagination.perPage,
              order_col: this.teamsSettings[this.$route.params.team].listsSettings.signedDocuments.order.col,
              order_dir: this.teamsSettings[this.$route.params.team].listsSettings.signedDocuments.order.dir,
            },
          });
          Promise.all([p1, p2])
            .then(() => {
              vm.initSort("uploads", true);
              vm.initSort("signedDocuments", true);
              vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
            })
            .catch(() => {
              vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
            });
        } else if (vm.$route.params.tab === "history") {
          const p1 = vm.getProjectsActivityFeeds({
            type: "Sorted",
            params: { project_uid: uid },
          });
          const p2 = vm.getProjectsActivityFeeds({
            params: { project_uid: uid, event_type: "FileDownload" },
          });
          const p3 = vm.getProjectsActivityFeeds({
            params: { project_uid: uid, event_type: "FileUpload" },
          });
          const p4 = vm.getProjectsActivityFeeds({
            params: { project_uid: uid, event_type: "FilePreview" },
          });
          const p5 = vm.getProjectsActivityFeeds({
            params: { project_uid: uid, event_type: "PageView" },
          });
          const p6 = vm.getProjectsActivityFeeds({
            params: { project_uid: uid, event_type: "ProjectMessage" },
          });
          const p7 = vm.getProjectsActivityFeeds({
            params: { project_uid: uid, event_type: "ProjectFileSigned" },
          });
          const p8 = vm.getProjectStats({ project: uid }).then(() => {
            if (vm.currentProjectStats.downloads.aggregated) {
              vm.setChartData(vm.currentProjectStats.downloads.aggregated, vm.fileDownloadsBarChartData);
              vm.setChartData(vm.currentProjectStats.downloads.aggregated, vm.fileDownloadsDoughnutChartData);
            }
            if (vm.currentProjectStats.pageviews.aggregated) {
              vm.setChartData(vm.currentProjectStats.pageviews.aggregated, vm.pageViewsBarChartData);
              vm.setChartData(vm.currentProjectStats.pageviews.aggregated, vm.pageViewsDoughnutChartData);
            }
            if (vm.currentProjectStats.previews.aggregated) {
              vm.setChartData(vm.currentProjectStats.previews.aggregated, vm.filePreviewsBarChartData);
              vm.setChartData(vm.currentProjectStats.previews.aggregated, vm.filePreviewsDoughnutChartData);
            }
            vm.visitorsDoughnutChartData.datasets[0].data.push(vm.currentProjectStats.visitors.total);
          });
          vm.getDataForLineChart(uid);
          Promise.all([p1, p2, p3, p4, p5, p6, p7, p8])
            .then(() => {
              vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
            })
            .catch(() => {
              vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
            });
        } else if (vm.$route.params.tab === "forms") {
          if (
            this.teamsSettings[this.$route.params.team] &&
            this.teamsSettings[this.$route.params.team].listsSettings.projectFormSubmissions.search
          ) {
            this.searchQuery = this.teamsSettings[this.$route.params.team].listsSettings.projectFormSubmissions.search;
          }
          this.filterDropdown = this.projectFormSubmissionsDropdownData.filterDropdown;
          this.mobileDropdown = this.projectFormSubmissionsDropdownData.mobileDropdown;
          this.sortMainColDropdown = this.projectFormSubmissionsDropdownData.sortMainColDropdown;
          let p = {
            search: vm.teamsSettings[vm.$route.params.team].listsSettings.projectFormSubmissions.search,
            page: vm.teamsSettings[vm.$route.params.team].listsSettings.projectFormSubmissions.pagination.page,
            per_page: vm.teamsSettings[vm.$route.params.team].listsSettings.projectFormSubmissions.pagination.perPage,
            order_col: vm.teamsSettings[vm.$route.params.team].listsSettings.projectFormSubmissions.order.col,
            order_dir: vm.teamsSettings[vm.$route.params.team].listsSettings.projectFormSubmissions.order.dir,
            client: vm.teamsSettings[vm.$route.params.team].listsSettings.projectFormSubmissions.filter.client.model,
            fieldset:
              vm.teamsSettings[vm.$route.params.team].listsSettings.projectFormSubmissions.filter.fieldset.model,
          };
          let params = vm.setFilterRequestObjectFromStore("projectFormSubmissions", p);
          const p1 = vm.getProjectFormSubmissions({
            project: vm.currentProject.uid,
            setListsSettings: true,
            params,
          });
          params = {
            per_page: "1000",
            order_col: "last_name",
            order_dir: "ASC",
            search: "",
          };
          const p2 = vm.getClientList({
            params,
            customLoading: true,
            setListsSettings: false,
          });
          const p3 = vm.getForms({
            customLoading: true,
            setListsSettings: false,
          });
          Promise.all([p1, p2, p3])
            .then(() => {
              vm.initSort("projectFormSubmissions");
              vm.initFilters("projectFormSubmissions");
              vm.setDropdownOptions();
              vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
            })
            .catch((err) => {
              console.log(err);
              vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
            });
        } else if (vm.$route.params.tab === "settings") {
          vm.getProjectSubscription({ project: uid })
            .then(() => {
              vm.isSubscribed = vm.currentProjectSubscription;
              vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
            })
            .catch(() => {
              vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
            });
        } else if (vm.$route.params.tab === "fileapproval") {
          vm.getFileApprovalList();
        } else {
          vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
        }
      });
    },
    getDataForLineChart(uid) {
      const vm = this;
      vm.lineChartIsLoading = true;
      vm.$store.commit("PROJECTS_RESET_ACTIVITY_FEED_ITEMS_LINE_CHART");
      function loadNextChunk(nextChunk = "") {
        vm.getProjectsActivityFeeds({
          type: "line-chart",
          customLoading: true,
          timezone: vm.teams[vm.$route.params.team].account.timezone,
          params: { project_uid: uid, from: nextChunk },
        })
          .then((res) => {
            console.log("RESPONSE", res);
            if (res.next_chunk_from) {
              loadNextChunk(res.next_chunk_from);
            } else {
              vm.lineChartIsLoading = false;
              vm.lineChartOptions.scales.yAxes[0].ticks.max = vm.activityFeedLineChart.maxValue;
            }
          })
          .catch(() => {
            vm.lineChartIsLoading = false;
          });
      }
      loadNextChunk();
    },
    loadMessagesAndSetRead(uid) {
      const vm = this;
      vm.getProjectMessages({ project: uid })
        .then(() => {
          vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
          vm.setNotificationStatus({
            type: "project_message",
            project: uid,
            params: {
              seen_at: true,
              read_at: true,
            },
          }).then(() => {
            vm.$eventbus.$emit("getMessagesCounter");
          });
        })
        .catch(() => {
          vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
        });
    },
    closeUppyAndRedirect() {
      this.$h.goTo("createSignedDocument", { uid: this.currentProject.uid });
    },
    removeMultiSelectValue(removedOption, id, index) {
      this.customFieldValue[index].forEach((val, key) => {
        if (val.value === removedOption.value) this.customFieldValue[index].splice(key, 1);
      });
    },
    setCustomLabel(client) {
      const name = this.$h.formatName(client);
      return `${name}`;
    },
    initChartsData() {
      this.fileDownloadsBarChartData = JSON.parse(JSON.stringify(this.initBarChartData));
      this.fileDownloadsDoughnutChartData = JSON.parse(JSON.stringify(this.initDoughnutChartData));
      this.filePreviewsBarChartData = JSON.parse(JSON.stringify(this.initBarChartData));
      this.filePreviewsDoughnutChartData = JSON.parse(JSON.stringify(this.initDoughnutChartData));
      this.pageViewsBarChartData = JSON.parse(JSON.stringify(this.initBarChartData));
      this.pageViewsDoughnutChartData = JSON.parse(JSON.stringify(this.initDoughnutChartData));
      this.visitorsDoughnutChartData = JSON.parse(JSON.stringify(this.initDoughnutChartData));
    },
    setChartData(stats, chart) {
      let counter = 0;
      let rest = 0;
      stats.forEach((val) => {
        if (counter < 10) {
          chart.extraLabels.push(val.title ? val.title : val.name);
          chart.labels.push(val.count);
          chart.datasets[0].data.push(val.count);
        } else {
          rest += val.count;
        }
        counter += 1;
      });
      if (rest > 0) {
        chart.extraLabels.push(this.$t("labels.others"));
        chart.labels.push(rest);
        chart.datasets[0].data.push(rest);
      }
    },
    calcChartHeight(count) {
      let height = 15;
      let factor = 70;
      for (let i = 0; i < count; i += 1) {
        height += factor;
        factor -= factor * 0.01;
      }
      return height;
    },
    sortProjectFiles(col, dir) {
      const vm = this;
      vm.getProjectFiles({
        project: this.currentProject.uid,
        setListsSettings: true,
        params: {
          type: "client",
          page: this.teamsSettings[this.$route.params.team].listsSettings.uploads.pagination.page,
          per_page: this.teamsSettings[this.$route.params.team].listsSettings.uploads.pagination.perPage,
          order_col: col,
          order_dir: dir,
        },
      }).then(() => {
        this.initSort("uploads");
      });
    },
    changeProjectFilesPage(page, perPage) {
      const vm = this;
      vm.getProjectFiles({
        project: vm.currentProject.uid,
        setListsSettings: true,
        params: {
          type: "client",
          page: page,
          per_page: perPage,
          order_col: vm.teamsSettings[this.$route.params.team].listsSettings.projects.order.col,
          order_dir: vm.teamsSettings[this.$route.params.team].listsSettings.projects.order.dir,
        },
      }).then(() => {
        //
      });
    },
    getFilePreview(uid) {
      const o = this.currentProjectFilesPreviews.find((obj) => {
        return obj.uid === uid;
      });
      return o ? o.url : "";
    },
    downloadFile(file) {
      this.downloadProjectFile({
        file: file.uid,
        project: this.currentProject.uid,
        redirect: false,
      }).then((res) => {
        window.location = res.data.data.redirect;
      });
    },
    // reloadSignedDocument () {
    //   const vm = this
    //   vm.getSignedDocument({
    //     project: vm.currentProject.uid,
    //     document: vm.currentSignedDocument.uid,
    //     customLoading: true,
    //     params: {
    //       page: vm.teamsSettings[vm.$route.params.team].listsSettings.signedDocuments.pagination.page,
    //       per_page: vm.teamsSettings[vm.$route.params.team].listsSettings.signedDocuments.pagination.perPage,
    //       order_col: vm.teamsSettings[vm.$route.params.team].listsSettings.signedDocuments.order.col,
    //       order_dir: vm.teamsSettings[vm.$route.params.team].listsSettings.signedDocuments.order.dir
    //     }
    //   }).then(() => {
    //     vm.currentSignedDocument = {}
    //   })
    // },
    changeSignedDocumentsPage(page, perPage) {
      const vm = this;
      vm.getSignedDocumentBundles({
        project: vm.currentProject.uid,
        setListsSettings: true,
        params: {
          page: page,
          per_page: perPage,
          order_col: vm.teamsSettings[vm.$route.params.team].listsSettings.signedDocuments.order.col,
          order_dir: vm.teamsSettings[vm.$route.params.team].listsSettings.signedDocuments.order.dir,
        },
      });
    },
    sortSignedDocuments(col, dir) {
      const vm = this;
      vm.getSignedDocumentBundles({
        project: vm.currentProject.uid,
        setListsSettings: true,
        params: {
          page: vm.teamsSettings[vm.$route.params.team].listsSettings.signedDocuments.pagination.page,
          per_page: vm.teamsSettings[vm.$route.params.team].listsSettings.signedDocuments.pagination.perPage,
          order_col: col,
          order_dir: dir,
        },
      }).then(() => {
        this.initSort("signedDocuments");
      });
    },
    copyLink(link, setSent = true) {
      clipboard.writeText(link);
      if (setSent) {
        this.setSentStatus();
      }
      this.$snotify.success(this.$t("toasts.copiedLink.success"), this.$t("toasts.success"), {
        icon: this.$successIcon,
      });
    },
    loadMessages(newer) {
      let page;
      newer ? (page = this.messagesPagination.current_page - 1) : (page = this.messagesPagination.current_page + 1);
      this.getProjectMessages({
        project: this.currentProject.uid,
        customLoading: true,
        params: {
          page: page,
        },
      });
    },
    validationCallback() {
      this.$snotify.error(this.$t("toasts.sentMessage.error"), this.$t("toasts.error"), {
        timeout: 0,
        icon: this.$errorIcon,
      });
    },
    openBuilder(preview) {
      this.$store.commit("GLOBAL_CUSTOM_LOADING_STARTED");
      this.getProjectBuilderSession({
        params: {
          project: this.$route.params.uid,
        },
        data: {
          return_url: window.location.href,
          preview: preview,
        },
      })
        .then(() => {
          location.href = this.builderSession;
        })
        .catch(() => {
          this.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
        });
    },
    setSentStatus(data) {
      const vm = this;
      if (
        (data && data.sendAlways) ||
        !(
          (this.currentProject.status & this.currentProjectStati["POSTED"].bit) ===
          this.currentProjectStati["POSTED"].bit
        )
      ) {
        vm.setProjectPosted({
          params: { project: vm.currentProject.uid },
          data,
        }).then(() => {
          vm.getProject({ project: vm.currentProject.uid });
        });
      }
    },
    toggleProjectDataForm() {
      if (this.editForm) {
        this.titleIsLoading = true;
        this.contactIsLoading = true;
        this.userIsLoading = true;
        this.notesIsLoading = true;
        let data = {
          title: this.title,
          contact_uid: this.contact && this.contact.uid,
          user_uid: this.user.uid,
          notes: this.notes,
        };
        this.$validator.validateAll("editProject").then((result) => {
          console.log("validator", result);
          if (result) {
            this.editProject({
              customLoading: true,
              params: { project: this.currentProject.uid },
              data,
            })
              .then(() => {
                this.$snotify.success(this.$t("toasts.editedProject.success"), this.$t("toasts.success"), {
                  icon: this.$successIcon,
                });
                this.editForm = false;
                this.editTitle = false;
                this.editContact = false;
                this.editUser = false;
                this.editNotes = false;
                this.titleIsLoading = false;
                this.contactIsLoading = false;
                this.userIsLoading = false;
                this.notesIsLoading = false;
                this.breadcrumbProjectItems[1].label = this.currentProject.title;
              })
              .catch((err) => {
                if (err.response && !err.response.data.error.custom) {
                  this.$snotify.error(this.$t("toasts.editedProject.error"), this.$t("toasts.error"), {
                    timeout: 0,
                    icon: this.$errorIcon,
                  });
                }
                this.titleIsLoading = false;
                this.contactIsLoading = false;
                this.userIsLoading = false;
                this.notesIsLoading = false;
              });
          } else {
            this.$snotify.error(this.$t("toasts.editedProjectTitle"), this.$t("toasts.error"), {
              timeout: 0,
              icon: this.$errorIcon,
            });
            this.titleIsLoading = false;
            this.contactIsLoading = false;
            this.userIsLoading = false;
            this.notesIsLoading = false;
          }
        });
      } else {
        this.editForm = true;
        this.editTitle = true;
        this.editContact = true;
        this.editUser = true;
        this.editNotes = true;
      }
    },
    toggleStatus(key, val) {
      const vm = this;
      let data = {};
      data.status = {};
      data.status[key] = val;
      vm.editProject({
        customLoading: true,
        params: { project: vm.currentProject.uid },
        data,
      })
        .then(() => {
          vm.getProject({
            customLoading: true,
            project: vm.currentProject.uid,
          });
          this.isArchived =
            (this.currentProject.status & this.currentProjectStati.ARCHIVED.bit) ===
            this.currentProjectStati.ARCHIVED.bit;
          vm.$snotify.success(vm.$t("toasts.changedStatus.success"), vm.$t("toasts.success"), {
            icon: vm.$successIcon,
          });
        })
        .catch((err) => {
          if (err.response && !err.response.data.error.custom) {
            vm.$snotify.error(vm.$t("toasts.changedStatus.error"), vm.$t("toasts.error"), {
              timeout: 0,
              icon: vm.$errorIcon,
            });
          }
        });
    },
    togglePwProtected() {
      let data = {
        private: this.pwProtected,
      };
      this.editProject({
        customLoading: true,
        params: { project: this.currentProject.uid },
        data,
      })
        .then(() => {
          this.$snotify.success(this.$t("toasts.editedProject.success"), this.$t("toasts.success"), {
            icon: this.$successIcon,
          });
        })
        .catch((err) => {
          if (err.response && !err.response.data.error.custom) {
            this.$snotify.error(this.$t("toasts.editedProject.error"), this.$t("toasts.error"), {
              timeout: 0,
              icon: this.$errorIcon,
            });
          }
        });
    },
    togglePwProtectedClient() {
      let data = {
        privacy_by_client_choice: this.pwProtectedClient,
      };
      this.editProject({
        customLoading: true,
        params: { project: this.currentProject.uid },
        data,
      })
        .then(() => {
          this.$snotify.success(this.$t("toasts.editedProject.success"), this.$t("toasts.success"), {
            icon: this.$successIcon,
          });
        })
        .catch((err) => {
          if (err.response && !err.response.data.error.custom) {
            this.$snotify.error(this.$t("toasts.editedProject.error"), this.$t("toasts.error"), {
              timeout: 0,
              icon: this.$errorIcon,
            });
          }
        });
    },
    toggleSubscription() {
      this.isSubscribed
        ? this.postProjectSubscription({
            customLoading: true,
            project: this.currentProject.uid,
          })
            .then(() => {
              this.$snotify.success(this.$t("toasts.setSubscription.success"), this.$t("toasts.success"), {
                icon: this.$successIcon,
              });
            })
            .catch((err) => {
              if (err.response && !err.response.data.error.custom) {
                this.$snotify.error(this.$t("toasts.setSubscription.error"), this.$t("toasts.error"), {
                  timeout: 0,
                  icon: this.$errorIcon,
                });
              }
            })
        : this.deleteProjectSubscription({
            customLoading: true,
            project: this.currentProject.uid,
          })
            .then(() => {
              this.$snotify.success(this.$t("toasts.deleteSubscription.success"), this.$t("toasts.success"), {
                icon: this.$successIcon,
              });
            })
            .catch((err) => {
              if (err.response && !err.response.data.error.custom) {
                this.$snotify.error(this.$t("toasts.deleteSubscription.error"), this.$t("toasts.error"), {
                  timeout: 0,
                  icon: this.$errorIcon,
                });
              }
            });
    },
    toggleCustomFields() {
      if (this.editCustomFields) {
        let customData = {};
        Object.keys(this.currentProjectCustomFields).forEach((key) => {
          this.$set(this.customFieldIsLoading, key, true);
          if (this.currentProjectCustomFields[key].type === "select") {
            if (this.customFieldValue[key]) customData[key] = [this.customFieldValue[key].value];
          } else if (this.currentProjectCustomFields[key].type === "multiselect") {
            customData[key] = [];
            this.customFieldValue[key].forEach((val) => {
              customData[key].push(val.value);
            });
          } else {
            customData[key] = this.customFieldValue[key];
          }
        });
        let data = {
          custom: customData,
        };
        this.editProject({
          customLoading: true,
          params: { project: this.currentProject.uid },
          data,
        })
          .then(() => {
            this.$snotify.success(this.$t("toasts.editedProject.success"), this.$t("toasts.success"), {
              icon: this.$successIcon,
            });
            this.editCustomFields = false;
            Object.keys(this.currentProjectCustomFields).forEach((key) => {
              this.$set(this.editCustomField, key, false);
              this.$set(this.customFieldIsLoading, key, false);
            });
          })
          .catch((err) => {
            if (err.response && !err.response.data.error.custom) {
              this.$snotify.error(this.$t("toasts.editedProject.error"), this.$t("toasts.error"), {
                timeout: 0,
                icon: this.$errorIcon,
              });
            }
            this.editCustomFields = false;
            Object.keys(this.currentProjectCustomFields).forEach((key) => {
              this.$set(this.customFieldIsLoading, key, false);
            });
          });
      } else {
        this.editCustomFields = true;
        Object.keys(this.currentProjectCustomFields).forEach((key) => {
          this.editCustomField[key] = true;
        });
      }
    },
    toggleCrmFields() {
      if (this.editCrmFields && this.currentProjectCrmFields) {
        let crmData = {};
        this.currentProjectCrmFields.forEach((group) => {
          Object.entries(group.fields).forEach(([key, field]) => {
            this.$set(this.crmFieldIsLoading, key, true);
            if (field.type === "select") {
              if (this.crmFieldValue[key]) crmData[key] = [this.crmFieldValue[key].value];
            } else if (field.type === "multiselect") {
              crmData[key] = [];
              this.crmFieldValue[key].forEach((val) => {
                crmData[key].push(val.value);
              });
            } else {
              crmData[key] = this.crmFieldValue[key];
            }
          });
        });
        let data = {
          custom: crmData,
        };
        this.editProject({
          customLoading: true,
          params: { project: this.currentProject.uid },
          data,
        })
          .then(() => {
            this.$snotify.success(this.$t("toasts.editedProject.success"), this.$t("toasts.success"), {
              icon: this.$successIcon,
            });
            this.editCrmFields = false;
            this.currentProjectCrmFields.forEach((group) => {
              Object.keys(group.fields).forEach((key) => {
                this.$set(this.editCrmField, key, false);
                this.$set(this.crmFieldIsLoading, key, false);
              });
            });
          })
          .catch((err) => {
            if (err.response && !err.response.data.error.custom) {
              this.$snotify.error(this.$t("toasts.editedProject.error"), this.$t("toasts.error"), {
                timeout: 0,
                icon: this.$errorIcon,
              });
            }
            this.editCrmFields = false;
            this.currentProjectCrmFields.forEach((group) => {
              Object.keys(group.fields).forEach((key) => {
                this.$set(this.crmFieldIsLoading, key, false);
              });
            });
          });
      } else {
        if (this.currentProjectCrmFields) {
          this.editCrmFields = true;
          this.currentProjectCrmFields.forEach((group) => {
            Object.keys(group.fields).forEach((key) => {
              this.editCrmField[key] = true;
            });
          });
        }
      }
    },
    showProjectDeleteModal() {
      this.$modal.show("delete-project-modal");
    },
    deleteProjectModalHandler() {
      this.$modal.hide("delete-project-modal");
      this.deleteProject({ project: this.currentProject.uid })
        .then(() => {
          this.$goTo("Projects");
          this.$snotify.success(this.$t("toasts.deleteProject.success"), this.$t("toasts.success"), {
            icon: this.$successIcon,
          });
        })
        .catch((err) => {
          if (err.response && !err.response.data.error.custom) {
            this.$snotify.error(this.$t("toasts.deleteProject.error"), this.$t("toasts.error"), {
              timeout: 0,
              icon: this.$errorIcon,
            });
          }
        });
    },
    showFileDeleteModal(uid) {
      this.currentDeleteFileUid = uid;
      this.$modal.show("delete-files-modal");
    },
    showSubmissionDeleteModal(uid) {
      this.currentDeleteSubmissionUid = uid;
      this.$modal.show("delete-submission-modal");
    },
    deleteFilesModalHandler(uid) {
      const vm = this;
      vm.currentDeleteFileUid = null;
      vm.$modal.hide("delete-files-modal");
      vm.deleteProjectFile({ project: vm.currentProject.uid, file: uid })
        .then(() => {
          const selectedFileIndex = vm.selectedUploadFiles.indexOf(uid);
          if (selectedFileIndex > -1) {
            vm.selectedUploadFiles.splice(selectedFileIndex, 1);
          }

          vm.$snotify.success(vm.$t("toasts.deleteFile.success"), vm.$t("toasts.success"), {
            icon: vm.$successIcon,
          });
          vm.getProjectFiles({
            project: vm.currentProject.uid,
            setListsSettings: true,
            params: {
              type: "client",
              page: vm.teamsSettings[vm.$route.params.team].listsSettings.uploads.pagination.page,
              per_page: vm.teamsSettings[vm.$route.params.team].listsSettings.uploads.pagination.perPage,
              order_col: vm.teamsSettings[vm.$route.params.team].listsSettings.uploads.order.col,
              order_dir: vm.teamsSettings[vm.$route.params.team].listsSettings.uploads.order.dir,
            },
          }).then(() => {
            //
          });
        })
        .catch((err) => {
          if (err.response && !err.response.data.error.custom) {
            vm.$snotify.error(vm.$t("toasts.deleteFile.error"), vm.$t("toasts.error"), {
              timeout: 0,
              icon: vm.$errorIcon,
            });
          }
        });
    },
    deleteMultipleFilesModalHandler() {
      let promises = [];
      const vm = this;
      const selectedFilesToDelete = vm.selectedUploadFiles.filter((fileUid) => {
        return vm.currentProjectFiles.some((file) => file.uid === fileUid);
      });

      vm.selectedUploadFiles = selectedFilesToDelete;
      vm.$modal.hide("delete-multiple-files-modal");
      vm.selectedUploadFiles.forEach((file) => {
        promises.push(vm.deleteProjectFile({ customLoading: true, project: vm.currentProject.uid, file: file }));
      });
      Promise.all(promises)
        .then(() => {
          vm.selectedUploadFiles = [];
          vm.$snotify.success(vm.$t("toasts.deleteMultipleFiles.success"), vm.$t("toasts.success"), {
            icon: vm.$successIcon,
          });
          vm.getProjectFiles({
            project: vm.currentProject.uid,
            setListsSettings: true,
            params: {
              type: "client",
              page: vm.teamsSettings[vm.$route.params.team].listsSettings.uploads.pagination.page,
              per_page: vm.teamsSettings[vm.$route.params.team].listsSettings.uploads.pagination.perPage,
              order_col: vm.teamsSettings[vm.$route.params.team].listsSettings.uploads.order.col,
              order_dir: vm.teamsSettings[vm.$route.params.team].listsSettings.uploads.order.dir,
            },
          });
        })
        .catch((err) => {
          if (err.response && !err.response.data.error.custom) {
            vm.$snotify.error(vm.$t("toasts.deleteMultipleFiles.error"), vm.$t("toasts.error"), {
              timeout: 0,
              icon: vm.$errorIcon,
            });
          }
        });
    },
    deleteSubmissionModalHandler(uid) {
      const vm = this;
      vm.currentDeleteSubmissionUid = null;
      vm.$modal.hide("delete-submission-modal");
      console.log("DELETE RESPONSE", uid);
      vm.deleteProjectFormSubmission({ project: vm.currentProject.uid, formResponse: uid })
        .then(() => {
          vm.$snotify.success(vm.$t("toasts.deleteSubmission.success"), vm.$t("toasts.success"), {
            icon: vm.$successIcon,
          });
          // GET SUBMISSIONS
          if (
            this.teamsSettings[this.$route.params.team] &&
            this.teamsSettings[this.$route.params.team].listsSettings.projectFormSubmissions.search
          ) {
            this.searchQuery = this.teamsSettings[this.$route.params.team].listsSettings.projectFormSubmissions.search;
          }
          this.filterDropdown = this.projectFormSubmissionsDropdownData.filterDropdown;
          this.mobileDropdown = this.projectFormSubmissionsDropdownData.mobileDropdown;
          this.sortMainColDropdown = this.projectFormSubmissionsDropdownData.sortMainColDropdown;
          let p = {
            search: vm.teamsSettings[vm.$route.params.team].listsSettings.projectFormSubmissions.search,
            page: vm.teamsSettings[vm.$route.params.team].listsSettings.projectFormSubmissions.pagination.page,
            per_page: vm.teamsSettings[vm.$route.params.team].listsSettings.projectFormSubmissions.pagination.perPage,
            order_col: vm.teamsSettings[vm.$route.params.team].listsSettings.projectFormSubmissions.order.col,
            order_dir: vm.teamsSettings[vm.$route.params.team].listsSettings.projectFormSubmissions.order.dir,
            client: vm.teamsSettings[vm.$route.params.team].listsSettings.projectFormSubmissions.filter.client.model,
            fieldset:
              vm.teamsSettings[vm.$route.params.team].listsSettings.projectFormSubmissions.filter.fieldset.model,
          };
          let params = vm.setFilterRequestObjectFromStore("projectFormSubmissions", p);
          const p1 = vm.getProjectFormSubmissions({
            project: vm.currentProject.uid,
            setListsSettings: true,
            customLoading: true,
            params,
          });
          Promise.all([p1])
            .then(() => {
              vm.initSort("projectFormSubmissions");
              vm.initFilters("projectFormSubmissions");
              vm.setDropdownOptions();
              vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
            })
            .catch((err) => {
              console.log(err);
              vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
            });
        })
        .catch((err) => {
          if (err.response && !err.response.data.error.custom) {
            vm.$snotify.error(vm.$t("toasts.deleteSubmission.error"), vm.$t("toasts.error"), {
              timeout: 0,
              icon: vm.$errorIcon,
            });
          }
        });
    },
    getSignedDocuments() {
      const vm = this;
      vm.getSignedDocumentBundles({
        project: vm.currentProject.uid,
        setListsSettings: true,
        params: {
          page: vm.teamsSettings[vm.$route.params.team].listsSettings.signedDocuments.pagination.page,
          per_page: vm.teamsSettings[vm.$route.params.team].listsSettings.signedDocuments.pagination.perPage,
          order_col: vm.teamsSettings[vm.$route.params.team].listsSettings.signedDocuments.order.col,
          order_dir: vm.teamsSettings[vm.$route.params.team].listsSettings.signedDocuments.order.dir,
        },
      });
    },
    showSignedDocumentFileEditModal(data) {
      this.selectedSignedDocumentFile = {
        name: data.file.title,
        uid: data.file.uid,
        document: data.document,
      };
      this.$modal.show("edit-signed-document-files-modal");
    },
    editSignedDocumentFilesModalHandler() {
      const vm = this;
      const data = {
        title: vm.signedDocumentFileName,
      };
      vm.$validator.validateAll().then((result) => {
        if (result) {
          vm.editSignedDocumentFile({
            params: {
              project: vm.currentProject.uid,
              document: vm.selectedSignedDocumentFile.document,
              file: vm.selectedSignedDocumentFile.uid,
            },
            data: data,
          })
            .then(() => {
              vm.getSignedDocuments();
              this.$modal.hide("edit-signed-document-files-modal");
              this.$snotify.success(this.$t("signedDocuments.toasts.editFile.success"), this.$t("toasts.success"), {
                icon: this.$successIcon,
              });
            })
            .catch(() => {
              vm.$snotify.error(vm.$t("signedDocuments.toasts.editFile.error"), vm.$t("toasts.error"), {
                timeout: 0,
                icon: vm.$errorIcon,
              });
            });
        } else {
          vm.$snotify.error(vm.$t("signedDocuments.toasts.editFile.validation"), vm.$t("toasts.error"), {
            timeout: 0,
            icon: vm.$errorIcon,
          });
        }
      });
    },
    showSignedDocumentUploadModal(document) {
      this.createParams = {
        project: this.currentProject.uid,
        document: document.uid,
      };
      this.uploadConfig.max_files = document.expected_files_count - document.files.length;
      this.openModal = true;
      this.currentSignedDocument = document;
    },
    closeSignedDocumentUploadModal() {
      this.openModal = false;
      this.$eventbus.$emit("resetUppy");
      this.getSignedDocuments();
    },
    createSignedDocumentFile(data) {
      var vm = this;
      return new Promise((resolve, reject) => {
        vm.createSignedDocumentFiles(data)
          .then((res) => {
            resolve(res);
          })
          .catch((err) => {
            reject(err);
          });
      });
    },
    startSigningProcess(data) {
      const vm = this;
      vm.startSignedDocumentSigningProcess({
        project: this.currentProject.uid,
        document: data.uid,
      }).then(() => {
        vm.getSignedDocuments();
      });
    },
    redirectToSigningPage(data) {
      if (
        this.teams[this.$route.params.team].account.team.attributes.signed_documents.providers[data.provider]
          .show_sign_action
      ) {
        const vm = this;
        vm.getSignedDocumentSigningUrl({
          project: this.currentProject.uid,
          document: data.uid,
        }).then((res) => {
          window.open(res.url, "_self");
        });
      }
    },
    downloadSignedDocumentFile(data) {
      const d = {
        file: data.file.uid,
        project: this.currentProject.uid,
        document: data.document,
        redirect: false,
      };
      this.getDownloadSignedDocumentFileUrl(d).then((res) => {
        window.location = res.data.data.redirect;
      });
    },
    showSignedDocumentDeleteModal(data) {
      this.selectedSignedDocument = data;
      this.$modal.show("delete-signed-document-modal");
    },
    deleteSignedDocumentModalHandler() {
      const vm = this;
      vm.$modal.hide("delete-signed-document-modal");
      vm.deleteSignedDocument({
        project: vm.currentProject.uid,
        document: vm.selectedSignedDocument.uid,
      })
        .then(() => {
          vm.$snotify.success(vm.$t("signedDocuments.toasts.deleteSignedDocument.success"), vm.$t("toasts.success"), {
            icon: vm.$successIcon,
          });
          vm.getSignedDocuments();
        })
        .catch((err) => {
          if (err.response && !err.response.data.error.custom) {
            vm.$snotify.error(vm.$t("signedDocuments.toasts.deleteSignedDocument.error"), vm.$t("toasts.error"), {
              timeout: 0,
              icon: vm.$errorIcon,
            });
          }
        });
    },
    showSignedDocumentFileDeleteModal(data) {
      this.selectedSignedDocumentFile = {
        name: data.file.title,
        uid: data.file.uid,
        document: data.document,
      };
      this.$modal.show("delete-signed-document-files-modal");
    },
    deleteSignedDocumentFilesModalHandler() {
      const vm = this;
      vm.$modal.hide("delete-signed-document-files-modal");
      vm.deleteSignedDocumentFile({
        project: vm.currentProject.uid,
        document: vm.selectedSignedDocumentFile.document,
        file: vm.selectedSignedDocumentFile.uid,
      })
        .then(() => {
          vm.$snotify.success(vm.$t("signedDocuments.toasts.deleteFile.success"), vm.$t("toasts.success"), {
            icon: vm.$successIcon,
          });
          vm.getSignedDocuments();
        })
        .catch((err) => {
          if (err.response && !err.response.data.error.custom) {
            vm.$snotify.error(vm.$t("signedDocuments.toasts.deleteFile.error"), vm.$t("toasts.error"), {
              timeout: 0,
              icon: vm.$errorIcon,
            });
          }
        });
    },
    setOrderBy(col, dir) {
      this.order = { col, dir };
    },
    sort(col, dir) {
      this.setOrderBy(col, dir);
      this.getProjectFiles({
        params: {
          type: "client",
          page: this.filesPagination.current_page,
          per_page: this.filesPagination.per_page,
          order_col: col,
          order_dir: dir,
        },
        project: this.currentProject.uid,
      });
      this.removeActiveSortingStates();
    },
    removeActiveSortingStates() {
      this.sortableDropdownsArray.forEach((dropdown) => {
        if (dropdown[0].col !== this.order.col) {
          dropdown[0].options.forEach((option) => {
            option.active = false;
          });
        }
      });
    },
    setActiveTab(slug) {
      this.$store.commit("GLOBAL_CUSTOM_LOADING_STARTED");
      switch (slug) {
        case this.$t("menus.overview").replace(/\s+/g, "-").toLowerCase():
          return this.$goTo("Project", {
            uid: this.$route.params.uid,
            tab: "overview",
          });
        case this.$t("menus.infos").replace(/\s+/g, "-").toLowerCase():
          return this.$goTo("Project", {
            uid: this.$route.params.uid,
            tab: "data",
          });
        case this.$t("menus.share").replace(/\s+/g, "-").toLowerCase():
          return this.$goTo("Project", {
            uid: this.$route.params.uid,
            tab: "share",
          });
        case this.$t("menus.uploads").replace(/\s+/g, "-").toLowerCase():
          return this.$goTo("Project", {
            uid: this.$route.params.uid,
            tab: "uploads",
          });
        case this.$t("menus.forms").replace(/\s+/g, "-").toLowerCase():
          return this.$goTo("Project", {
            uid: this.$route.params.uid,
            tab: "forms",
          });
        case this.$t("menus.activityFeeds").replace(/\s+/g, "-").toLowerCase():
          this.$store.commit("PROJECTS_RESET_ACTIVITY_FEEDS");
          return this.$goTo("Project", {
            uid: this.$route.params.uid,
            tab: "history",
            subtab: "all",
          });
        case this.$t("menus.extended-data").replace(/\s+/g, "-").toLowerCase():
          return this.$goTo("Project", {
            uid: this.$route.params.uid,
            tab: "extended-data",
          });
        case this.$t("menus.crm").replace(/\s+/g, "-").toLowerCase():
          return this.$goTo("Project", {
            uid: this.$route.params.uid,
            tab: "crm",
          });
        case this.$t("menus.fileapproval").replace(/\s+/g, "-").toLowerCase():
          return this.$goTo("Project", {
            uid: this.$route.params.uid,
            tab: "fileapproval",
          });
        case this.activityFeedTabs[0].replace(/\s+/g, "-").toLowerCase():
          this.$store.commit("PROJECTS_RESET_ACTIVITY_FEEDS");
          return this.$goTo("Project", {
            uid: this.$route.params.uid,
            tab: "history",
            subtab: "all",
          });
        case this.activityFeedTabs[1].replace(/\s+/g, "-").toLowerCase():
          this.$store.commit("PROJECTS_RESET_ACTIVITY_FEEDS");
          return this.$goTo("Project", {
            uid: this.$route.params.uid,
            tab: "history",
            subtab: "fileuploads",
          });
        case this.activityFeedTabs[2].replace(/\s+/g, "-").toLowerCase():
          this.$store.commit("PROJECTS_RESET_ACTIVITY_FEEDS");
          return this.$goTo("Project", {
            uid: this.$route.params.uid,
            tab: "history",
            subtab: "filedownloads",
          });
        case this.activityFeedTabs[3].replace(/\s+/g, "-").toLowerCase():
          this.$store.commit("PROJECTS_RESET_ACTIVITY_FEEDS");
          return this.$goTo("Project", {
            uid: this.$route.params.uid,
            tab: "history",
            subtab: "filepreviews",
          });
        case this.activityFeedTabs[4].replace(/\s+/g, "-").toLowerCase():
          this.$store.commit("PROJECTS_RESET_ACTIVITY_FEEDS");
          return this.$goTo("Project", {
            uid: this.$route.params.uid,
            tab: "history",
            subtab: "pageviews",
          });
        case this.activityFeedTabs[5].replace(/\s+/g, "-").toLowerCase():
          this.$store.commit("PROJECTS_RESET_ACTIVITY_FEEDS");
          return this.$goTo("Project", {
            uid: this.$route.params.uid,
            tab: "history",
            subtab: "projectmessages",
          });
        case this.activityFeedTabs[6].replace(/\s+/g, "-").toLowerCase():
          this.$store.commit("PROJECTS_RESET_ACTIVITY_FEEDS");
          return this.$goTo("Project", {
            uid: this.$route.params.uid,
            tab: "history",
            subtab: "projectfilesignings",
          });
        case this.$t("menus.settings").replace(/\s+/g, "-").toLowerCase():
          return this.$goTo("Project", {
            uid: this.$route.params.uid,
            tab: "settings",
          });
      }
    },
    getActiveTab() {
      switch (this.$route.params.tab) {
        case "overview":
          return this.$t("menus.overview").replace(/\s+/g, "-").toLowerCase();
        case "data":
          return this.$t("menus.infos").replace(/\s+/g, "-").toLowerCase();
        case "share":
          return this.$t("menus.share").replace(/\s+/g, "-").toLowerCase();
        case "uploads":
          return this.$t("menus.uploads").replace(/\s+/g, "-").toLowerCase();
        case "forms":
          return this.$t("menus.forms").replace(/\s+/g, "-").toLowerCase();
        case "history":
          return this.$t("menus.activityFeeds").replace(/\s+/g, "-").toLowerCase();
        case "extended-data":
          return this.$t("menus.extended-data").replace(/\s+/g, "-").toLowerCase();
        case "crm":
          return this.$t("menus.crm").replace(/\s+/g, "-").toLowerCase();
        case "fileapproval":
          return this.$t("menus.fileapproval").replace(/\s+/g, "-").toLowerCase();
        case "settings":
          return this.$t("menus.settings").replace(/\s+/g, "-").toLowerCase();
      }
    },
    getActiveActivityFeedsTab() {
      switch (this.$route.params.subtab) {
        case "all":
          return this.activityFeedTabs[0].replace(/\s+/g, "-").toLowerCase();
        case "fileuploads":
          return this.activityFeedTabs[1].replace(/\s+/g, "-").toLowerCase();
        case "filedownloads":
          return this.activityFeedTabs[2].replace(/\s+/g, "-").toLowerCase();
        case "filepreviews":
          return this.activityFeedTabs[3].replace(/\s+/g, "-").toLowerCase();
        case "pageviews":
          return this.activityFeedTabs[4].replace(/\s+/g, "-").toLowerCase();
        case "projectmessages":
          return this.activityFeedTabs[5].replace(/\s+/g, "-").toLowerCase();
        case "projectfilesignings":
          return this.activityFeedTabs[6].replace(/\s+/g, "-").toLowerCase();
      }
    },
    loadMore(type, from) {
      this.loadMoreIsLoading = true;
      type === "Sorted" &&
        this.getProjectsActivityFeeds({
          type,
          params: { project_uid: this.$route.params.uid, from },
          customLoading: true,
        })
          .then(() => {
            this.loadMoreIsLoading = false;
          })
          .catch(() => {
            this.loadMoreIsLoading = false;
          });
      type === "FileDownload" &&
        this.getProjectsActivityFeeds({
          params: {
            project_uid: this.$route.params.uid,
            event_type: type,
            from,
          },
          customLoading: true,
        })
          .then(() => {
            this.loadMoreIsLoading = false;
          })
          .catch(() => {
            this.loadMoreIsLoading = false;
          });
      type === "FileUpload" &&
        this.getProjectsActivityFeeds({
          params: {
            project_uid: this.$route.params.uid,
            event_type: type,
            from,
          },
          customLoading: true,
        })
          .then(() => {
            this.loadMoreIsLoading = false;
          })
          .catch(() => {
            this.loadMoreIsLoading = false;
          });
      type === "FilePreview" &&
        this.getProjectsActivityFeeds({
          params: {
            project_uid: this.$route.params.uid,
            event_type: type,
            from,
          },
          customLoading: true,
        })
          .then(() => {
            this.loadMoreIsLoading = false;
          })
          .catch(() => {
            this.loadMoreIsLoading = false;
          });
      type === "PageView" &&
        this.getProjectsActivityFeeds({
          params: {
            project_uid: this.$route.params.uid,
            event_type: type,
            from,
          },
          customLoading: true,
        })
          .then(() => {
            this.loadMoreIsLoading = false;
          })
          .catch(() => {
            this.loadMoreIsLoading = false;
          });
      type === "ProjectMessage" &&
        this.getProjectsActivityFeeds({
          params: {
            project_uid: this.$route.params.uid,
            event_type: type,
            from,
          },
          customLoading: true,
        })
          .then(() => {
            this.loadMoreIsLoading = false;
          })
          .catch(() => {
            this.loadMoreIsLoading = false;
          });
      type === "ProjectFileSigned" &&
        this.getProjectsActivityFeeds({
          params: {
            project_uid: this.$route.params.uid,
            event_type: type,
            from,
          },
          customLoading: true,
        })
          .then(() => {
            this.loadMoreIsLoading = false;
          })
          .catch(() => {
            this.loadMoreIsLoading = false;
          });
    },
    formatTagsOptions(tagsObjects) {
      this.tagsOptions = [];
      Object.keys(tagsObjects).forEach((key) => {
        this.tagsOptions.push(tagsObjects[key].name);
      });
    },
    addTag(newTag) {
      this.tagsOptions.push(newTag);
      this.tags.push(newTag);
    },
    initTagOptions() {
      this.loadTagsIsLoading = true;
      const params = {
        per_page: "1000",
      };
      this.getTeamTags({ params, customLoading: true }).then((res) => {
        this.formatTagsOptions(JSON.parse(JSON.stringify(res)));
        this.loadTagsIsLoading = false;
      });
    },
    toggleEditTags() {
      if (this.showEditTags) {
        this.editTagsIsLoading = true;
        var data = {};
        this.editProject({
          customLoading: true,
          params: { project: this.currentProject.uid },
          data,
          tags: { tags: this.tags },
        })
          .then(() => {
            this.getProjectTags({ project: this.currentProject.uid }).then(() => {
              this.formatTagsOptions(JSON.parse(JSON.stringify(this.currentProjectTags)));
              this.tags = JSON.parse(JSON.stringify(this.tagsOptions));
              this.initTagOptions();
            });
            this.$snotify.success(this.$t("toasts.editedProject.success"), this.$t("toasts.success"), {
              icon: this.$successIcon,
            });
            this.showEditTags = !this.showEditTags;
            this.editTagsIsLoading = false;
          })
          .catch((err) => {
            if (err.response && !err.response.data.error.custom) {
              this.$snotify.error(this.$t("toasts.editedProject.error"), this.$t("toasts.error"), {
                timeout: 0,
                icon: this.$errorIcon,
              });
            }
            this.editTagsIsLoading = false;
          });
      } else {
        this.showEditTags = true;
      }
    },

    switchCustomerEngagementScoreRange(range) {
      let vm = this;
      vm.customerEngagementScoreRange = range;
    },

    getFileApprovalList() {
      let vm = this;

      let payload = {
        apiMethod: "fileApprovalList",
        params: {
          project: vm.currentProject.uid,
        },
      };

      vm.fileApprovalApiRequest(payload)
        .then((fileApprovals) => {
          fileApprovals.forEach(function (fileApproval) {
            if (fileApproval.versions.length > 0) {
              fileApproval.versions[0].isLatestVersion = true;
            }
          });

          vm.fileApprovals = fileApprovals;

          vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
        })
        .catch((err) => {
          console.log("error: ", err);
        });
    },

    resetNewFileApprovalFormAndGoBack() {
      this.newFileApprovalTitle = "";
      this.createNewFileApprovalActive = false;
    },

    createFileApproval() {
      let vm = this;
      let fileApprovalName = vm.newFileApprovalTitle;

      let payload = {
        apiMethod: "fileApprovalCreate",
        params: {
          project: vm.currentProject.uid,
        },
        data: {
          name: fileApprovalName,
        },
      };

      vm.fileApprovalApiRequest(payload)
        .then((fileApproval) => {
          vm.$snotify.success(vm.$t("fileapproval.toasts.createFileApproval.success"), vm.$t("toasts.success"), {
            icon: vm.$successIcon,
          });
          console.log("new file approval created", fileApproval);

          vm.newFileApprovalTitle = "";
          vm.createNewFileApprovalActive = false;

          vm.getFileApprovalList();
        })
        .catch(() => {
          vm.$snotify.error(vm.$t("fileapproval.toasts.createFileApproval.error"), vm.$t("toasts.error"), {
            timeout: 0,
            icon: vm.$errorIcon,
          });
          //vm.$store.commit("GLOBAL_FILE_APPROVAL_LOADING_ENDED");
        });
    },

    fileApprovalListReloadListener() {
      let vm = this;

      vm.getFileApprovalList();
    },
    openFileViewer(uid) {
      this.currentFileViewerUid = uid;
      this.showFileViewer = true;
    },
    setRedirectedInboxProjectAndRedirect() {
      this.setRedirectedInboxProject(this.currentProject);
      this.$goTo("Inbox");
    },
    sortProjectForms(col, dir) {
      const params = {
        search: this.searchQuery,
        page: this.teamsSettings[this.$route.params.team].listsSettings.projectForms.pagination.page,
        per_page: this.teamsSettings[this.$route.params.team].listsSettings.projectForms.pagination.perPage,
        order_col: col,
        order_dir: dir,
      };
      var vm = this;
      this.getProjectForms(params)
        .then(() => {
          vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
        })
        .catch((err) => {
          console.log(err);
          vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
        });
    },
    changeProjectFormsPage(page, perPage) {
      const params = {
        search: this.searchQuery,
        page,
        per_page: perPage,
        order_col: this.teamsSettings[this.$route.params.team].listsSettings.projectForms.order.col,
        order_dir: this.teamsSettings[this.$route.params.team].listsSettings.projectForms.order.dir,
      };
      this.getCurrentProjectForms(params);
    },
    setProjectFormsFilters() {
      const params = {
        search: this.searchQuery,
        page: 1,
        per_page: this.teamsSettings[this.$route.params.team].listsSettings.projectForms.pagination.perPage,
        order_col: this.teamsSettings[this.$route.params.team].listsSettings.projectForms.order.col,
        order_dir: this.teamsSettings[this.$route.params.team].listsSettings.projectForms.order.dir,
      };
      this.getCurrentProjectForms(params);
      this.$eventbus.$emit("closeAllDropdowns");
    },
    getCurrentProjectForms(params) {
      this.filterDropdown[0].options.forEach((option) => {
        if (option.active && option.switch && option.switch.name && !option.select) {
          params[option.switch.name] = option.switch.model;
        }
        if (option.active && option.select && !option.select.custom) {
          params[option.select.name] = this.transformModelToRequestArray(option.select.model);
        }
      });
      this.getProjectForms({
        setListsSettings: true,
        params,
      }).then(() => {
        this.initSort("projectForms");
      });
    },
    sortProjectFormSubmissions(col, dir) {
      const params = {
        search: this.searchQuery,
        page: this.teamsSettings[this.$route.params.team].listsSettings.projectFormSubmissions.pagination.page,
        per_page: this.teamsSettings[this.$route.params.team].listsSettings.projectFormSubmissions.pagination.perPage,
        order_col: col,
        order_dir: dir,
      };
      this.getSubmissions(params);
    },
    changeProjectFormSubmissionsPage(page, perPage) {
      const params = {
        search: this.searchQuery,
        page,
        per_page: perPage,
        order_col: this.teamsSettings[this.$route.params.team].listsSettings.projectFormSubmissions.order.col,
        order_dir: this.teamsSettings[this.$route.params.team].listsSettings.projectFormSubmissions.order.dir,
      };
      this.getSubmissions(params);
    },
    setProjectFormSubmissionsFilters() {
      const params = {
        search: this.searchQuery,
        page: 1,
        per_page: this.teamsSettings[this.$route.params.team].listsSettings.projectFormSubmissions.pagination.perPage,
        order_col: this.teamsSettings[this.$route.params.team].listsSettings.projectFormSubmissions.order.col,
        order_dir: this.teamsSettings[this.$route.params.team].listsSettings.projectFormSubmissions.order.dir,
      };
      this.getSubmissions(params);
      this.$eventbus.$emit("closeAllDropdowns");
    },
    getSubmissions(params) {
      this.filterDropdown[0].options.forEach((option) => {
        if (option.active && option.switch && option.switch.name && !option.select) {
          params[option.switch.name] = option.switch.model;
        }
        if (option.active && option.select && !option.select.custom) {
          params[option.select.name] = this.transformModelToRequestArray(option.select.model);
        }
      });
      this.getProjectFormSubmissions({
        project: this.currentProject.uid,
        setListsSettings: true,
        params,
      }).then(() => {
        this.initSort("projectFormSubmissions");
      });
    },
    setCurrentSubmission(submission) {
      const vm = this;
      vm.getProjectFormSubmission({
        project: vm.currentProject.uid,
        formResponse: submission.uid,
      });
    },
    transformCheckboxValue(val) {
      return (typeof val === "boolean" && val) || val.includes("1") || val.includes("true");
    },
    resetCurrentProjectUploads() {
      this.$store.commit("GLOBAL_RESET_LIST_SETTINGS", "uploads");
      this.$store.commit("PROJECTS_RESET_CURRENT_PROJECT_FILES");
    },
    resetCurrentProjectFormSubmissions() {
      this.$store.commit("GLOBAL_RESET_LIST_SETTINGS", "formSubmissions");
      this.$store.commit("FORMS_RESET_CURRENT_PROJECT_FORM_SUBMISSION");
    },
    loadClients(query) {
      if (query.length > 1) {
        this.isLoading = true;
        const params = {
          per_page: "1000",
          search: query,
        };
        this.getClientList({ params, customLoading: true }).then((res) => {
          this.clientOptions = res;
          this.isLoading = false;
        });
      } else if (query.length === 0) {
        this.isLoading = true;
        const params = {
          per_page: "1000",
          order_col: "last_name",
          order_dir: "ASC",
          search: "",
        };
        this.getClientList({ params, customLoading: true }).then((res) => {
          this.clientOptions = res;
          this.isLoading = false;
        });
      }
    },
    addGuest() {
      const vm = this;
      this.$validator.validateAll("addGuest").then((result) => {
        if (result) {
          vm.addProjectGuest({ project: vm.currentProject.uid, data: { client: vm.projectGuest.uid } })
            .then(() => {
              vm.getProjectGuests({ project: vm.currentProject.uid });
              vm.$snotify.success(vm.$t("toasts.addGuest.success"), vm.$t("toasts.success"), {
                icon: vm.$successIcon,
              });
            })
            .catch(() => {
              vm.$snotify.error(vm.$t("toasts.addGuest.error"), vm.$t("toasts.error"), {
                timeout: 0,
                icon: vm.$errorIcon,
              });
            });
        }
      });
    },
    openDeleteGuestModal(guest) {
      this.currentDeleteGuest = guest;
      this.$modal.show("delete-guest-modal");
    },
    removeGuest() {
      const vm = this;
      vm.deleteProjectGuest({ project: vm.currentProject.uid, guest: vm.currentDeleteGuest.uid })
        .then(() => {
          vm.$snotify.success(vm.$t("toasts.removeGuest.success"), vm.$t("toasts.success"), {
            icon: vm.$successIcon,
          });
          vm.currentDeleteGuest = null;
          vm.$modal.hide("delete-guest-modal");
          vm.getProjectGuests({ project: vm.currentProject.uid });
        })
        .catch(() => {
          vm.$snotify.error(vm.$t("toasts.removeGuest.error"), vm.$t("toasts.error"), {
            timeout: 0,
            icon: vm.$errorIcon,
          });
        });
    },
    selectFile(file) {
      const index = this.selectedUploadFiles.indexOf(file.uid);
      if (index > -1) {
        this.selectedUploadFiles.splice(index, 1);
      } else {
        this.selectedUploadFiles.push(file.uid);
      }
    },
    deleteSelectedUploadFiles() {
      if (this.selectedUploadFiles.length > 0) {
        this.$modal.show("delete-multiple-files-modal");
      }
    },
  },
  computed: {
    dailyProjectCustomerEngagementScore() {
      let vm = this;
      if (!vm.currentProject.customer_engagement_score) {
        return 0;
      }

      if (!vm.currentProject.customer_engagement_score.daily) {
        return 0;
      }

      return vm.currentProject.customer_engagement_score.daily;
    },
    totalProjectCustomerEngagementScore() {
      let vm = this;
      if (!vm.currentProject.customer_engagement_score) {
        return 0;
      }

      if (!vm.currentProject.customer_engagement_score.total) {
        return 0;
      }

      return vm.currentProject.customer_engagement_score.total;
    },
    filteredCurrentProjectStati() {
      return this.currentProjectStati
        ? Object.fromEntries(
            Object.entries(this.currentProjectStati).filter(
              ([key]) => !this.currentProjectStati[key].readonly && key !== "ARCHIVED"
            )
          )
        : null;
    },
    filteredCurrentProjectCustomFields() {
      return this.currentProjectCustomFields
        ? Object.fromEntries(
            Object.entries(this.currentProjectCustomFields).filter(([, field]) => field.visible === true)
          )
        : null;
    },
    filteredCurrentProjectCrmFields() {
      const filteredData = [];
      for (const formGroup of this.currentProjectCrmFields) {
        const filteredFields = Object.fromEntries(
          Object.entries(formGroup.fields).filter(([, field]) => field.visible === true)
        );
        if (Object.keys(filteredFields).length > 0) {
          const newFormGroup = { ...formGroup, fields: filteredFields };
          filteredData.push(newFormGroup);
        }
      }
      return filteredData;
    },
    isVisitorStatsEnabled() {
      return (
        this.teams[this.$route.params.team].account &&
        this.teams[this.$route.params.team].account.team.attributes.visitor_stats
      );
    },
    isCustomerEngagementScoreEnabled() {
      return (
        this.teams[this.$route.params.team].account &&
        this.teams[this.$route.params.team].account.team.attributes.customerengagementscore
      );
    },
    isFileApprovalEnabled() {
      return (
        this.teams[this.$route.params.team] &&
        this.teams[this.$route.params.team].account &&
        this.teams[this.$route.params.team].account.team.attributes.file_approval
      );
    },
    isFormsEnabled() {
      return (
        this.teams[this.$route.params.team].account && this.teams[this.$route.params.team].account.team.attributes.forms
      );
    },
    hasCrmFieldsEnabled() {
      return (
        this.teams[this.$route.params.team].account.team.attributes.crm_fields &&
        this.teams[this.$route.params.team].account.team.attributes.crm_fields.length > 0
      );
    },
    visitorSyncDisabled() {
      return this.visitorSyncTimestamp && (new Date() - this.visitorSyncTimestamp) / 1000 / 60 < 1;
    },
    reassignProjectsEnabled() {
      let authenticatedUser = this.teams[this.$route.params.team].account;

      return authenticatedUser.abilities.indexOf("reassign_projects") > -1;
    },
    customerEngagementScoreDailyLinkColor() {
      let vm = this;
      if (vm.customerEngagementScoreRange != "daily") {
        return "rgb(56 189 248)";
      }
      return "#000000";
    },
    customerEngagementScoreTotalLinkColor() {
      let vm = this;
      if (vm.customerEngagementScoreRange != "total") {
        return "rgb(56 189 248)";
      }
      return "#000000";
    },
    currentLineChartData() {
      return this.activityFeedLineChart.data[this.activityFeedLineChart.days[this.lineChartCurrentDayIndex]];
    },
    disabledTabs() {
      return !this.hasCrmFieldsEnabled ? [this.$t("menus.crm").replace(/\s+/g, "-").toLowerCase()] : [];
    },
    ...mapGetters({
      showTips: "global_get_show_tips",
      showAlways: "global_get_show_tips_icon",
      teamsSettings: "global_get_teams_settings",
      teams: "auth_get_teams",
      currentProject: "projects_get_current_project",
      currentProjectCustomFields: "projects_get_current_project_custom_fields",
      currentProjectCrmFields: "projects_get_current_project_crm_fields",
      currentProjectStati: "projects_get_current_project_stati",
      currentProjectMessages: "projects_get_current_project_messages",
      currentProjectFiles: "projects_get_current_project_files",
      currentProjectStats: "projects_get_current_project_stats",
      currentProjectSubscription: "subscriptions_get_current_project_subscription",
      currentProjectTags: "projects_get_current_project_tags",
      builderSession: "projects_get_builder_session",
      contacts: "teams_get_contacts",
      messagesPagination: "projects_get_messages_pagination",
      activityFeedItems: "projects_get_activity_feed_items",
      activityFeedItemsGrouped: "projects_get_activity_feed_items_grouped",
      activityFeedFileDownloads: "projects_get_activity_feed_filedownloads",
      activityFeedFileUploads: "projects_get_activity_feed_fileuploads",
      activityFeedFilePreviews: "projects_get_activity_feed_filepreviews",
      activityFeedPageViews: "projects_get_activity_feed_pageviews",
      activityFeedProjectMessages: "projects_get_activity_feed_projectmessages",
      activityFeedProjectFileSignings: "projects_get_activity_feed_projectfilesignings",
      activityFeedLineChart: "projects_get_activity_feed_items_line_chart",
      statiBits: "global_get_fileStatiBits",
      signedDocumentBundles: "signed_documents_get_bundles",
      signedDocumentBundleFiles: "signed_documents_get_bundle_files",
      signedDocumentStatiBits: "global_get_signedDocumentStatiBits",
      visitorSyncTimestamp: "global_get_visitor_sync_timestamp",
      visitorSyncRunning: "global_get_visitor_sync_running",
      projectFormSubmissions: "forms_get_project_form_submissions",
      clients: "clients_get_client_list",
      fieldsets: "forms_get_forms",
      currentProjectFormSubmission: "forms_get_current_project_form_submission",
      currentProjectGuests: "projects_get_current_project_guests",
      previousCurrentProject: "projects_get_previous_current_project",
    }),
  },
};
</script>
